import { actionGameImages, brandLogos, referenceImages } from './brandAssets';
import { getOrderedTimelinePictogram } from './timelineAssets';

export type EditableItem = Record<string, string>;
export type EditableSectionValue = string | string[] | EditableItem[];
export type EditableSection = Record<string, EditableSectionValue>;
export type EditablePageContent = Record<string, EditableSection>;
export type SitePagesContent = Record<string, EditablePageContent>;

export type AdminPageItemField = {
  key: string;
  label: string;
  type?: 'text' | 'textarea' | 'image';
};

export type AdminPageField = {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'textList' | 'image' | 'imageList' | 'items';
  description?: string;
  itemFields?: AdminPageItemField[];
};

export type AdminPageSection = {
  key: string;
  title: string;
  description?: string;
  fields: AdminPageField[];
};

export type AdminPageGroup = {
  key: string;
  title: string;
  path: string;
  sections: AdminPageSection[];
};

type ImageContent = Record<string, any>;
type BrandContent = Record<string, any>;

const text = (key: string, label: string, description?: string): AdminPageField => ({ key, label, type: 'text', description });
const textarea = (key: string, label: string, description?: string): AdminPageField => ({ key, label, type: 'textarea', description });
const textList = (key: string, label: string, description?: string): AdminPageField => ({ key, label, type: 'textList', description });
const image = (key: string, label: string, description?: string): AdminPageField => ({ key, label, type: 'image', description });
const imageList = (key: string, label: string, description?: string): AdminPageField => ({ key, label, type: 'imageList', description });
const items = (key: string, label: string, itemFields: AdminPageItemField[], description?: string): AdminPageField => ({
  key,
  label,
  type: 'items',
  itemFields,
  description,
});

const titleTextFields: AdminPageItemField[] = [
  { key: 'title', label: 'Titre' },
  { key: 'text', label: 'Texte', type: 'textarea' },
];

const titleTextImageFields: AdminPageItemField[] = [
  { key: 'title', label: 'Titre' },
  { key: 'text', label: 'Texte', type: 'textarea' },
  { key: 'image', label: 'Image', type: 'image' },
];

const statFields: AdminPageItemField[] = [
  { key: 'value', label: 'Valeur' },
  { key: 'unit', label: 'Unité' },
  { key: 'label', label: 'Libellé' },
  { key: 'icon', label: 'Icône', type: 'image' },
];

const faqFields: AdminPageItemField[] = [
  { key: 'question', label: 'Question' },
  { key: 'answer', label: 'Réponse', type: 'textarea' },
];

const selectionFields: AdminPageItemField[] = [
  { key: 'title', label: 'Titre' },
  { key: 'subtitle', label: 'Sous-titre' },
  { key: 'text', label: 'Texte', type: 'textarea' },
];

const timelineStepFields: AdminPageItemField[] = [
  { key: 'time', label: 'Heure (ex: 09h00)' },
  { key: 'label', label: 'Étape' },
  { key: 'image', label: 'Image pictogramme', type: 'image' },
  { key: 'icon', label: 'Icône Lucide de secours (Coffee, Trophy, Gamepad2…)' },
  { key: 'highlight', label: 'Mettre en valeur ? (écrire oui)' },
];

const priceRowFields: AdminPageItemField[] = [
  { key: 'label', label: 'Libellé' },
  { key: 'price', label: 'Prix' },
];

function defaultImages(images?: ImageContent) {
  return {
    home: {
      hero: images?.home?.hero || '/brand/action-game/home-hero.webp',
      sidebarFeature: images?.home?.sidebarFeature || '/brand/action-game/home-detail.webp',
    },
    infosPratiques: {
      hero: images?.infosPratiques?.hero || referenceImages.teamBuilding.intro,
    },
    actionGame: {
      hero: images?.actionGame?.hero || '/brand/action-game/bataille-navale-hero.webp',
      intro: images?.actionGame?.intro || '/brand/action-game/home-activity.webp',
      adultCard: images?.actionGame?.adultCard || '/brand/action-game/demineur-card.webp',
      juniorCard: images?.actionGame?.juniorCard || '/brand/action-game/serpent-card.webp',
      stats: images?.actionGame?.stats || '/brand/action-game/home-detail.webp',
      gallery: Array.isArray(images?.actionGame?.gallery) ? images.actionGame.gallery : [...actionGameImages.gallery],
    },
    karaoke: {
      hero: images?.karaoke?.hero || '/brand/karaoke/karaoke-room-group-unsplash.webp',
    },
    nouvelleActivite: {
      hero: images?.nouvelleActivite?.hero || referenceImages.teamBuilding.gallery[0],
    },
    teamBuilding: {
      hero: images?.teamBuilding?.hero || '/brand/business/team-building-hero.webp',
      intro: images?.teamBuilding?.intro || '/brand/business/gallery-06.webp',
      gallery: Array.isArray(images?.teamBuilding?.gallery) ? images.teamBuilding.gallery : [
        '/brand/business/gallery-06.webp',
        '/brand/business/gallery-05.webp',
        '/brand/business/gallery-08.webp',
        '/brand/business/gallery-02.webp',
        '/brand/business/gallery-10.webp',
        '/brand/business/team-building-ref-1.webp',
      ],
    },
    privatisation: {
      hero: images?.privatisation?.hero || '/brand/business/privatisation-hero.webp',
      intro: images?.privatisation?.intro || '/brand/business/animation-bg.webp',
      gallery: Array.isArray(images?.privatisation?.gallery) ? images.privatisation.gallery : [
        '/brand/business/gallery-03.webp',
        '/brand/business/gallery-07.webp',
        '/brand/business/gallery-09.webp',
        '/brand/business/privatisation-ref-1.webp',
        '/brand/business/privatisation-ref-2.webp',
        '/brand/business/privatisation-ref-3.webp',
      ],
    },
    seminaire: {
      hero: images?.seminaire?.hero || '/brand/business/seminaire-hero.webp',
      intro: images?.seminaire?.intro || '/brand/business/seminaire-intro.webp',
      gallery: Array.isArray(images?.seminaire?.gallery) ? images.seminaire.gallery : [
        '/brand/business/seminaire-intro.webp',
        '/brand/business/gallery-01.webp',
        '/brand/business/gallery-04.webp',
        '/brand/business/seminaire-bg.webp',
        '/brand/business/animation-bg.webp',
        '/brand/business/gallery-09.webp',
      ],
    },
    evgEvjf: {
      hero: images?.evgEvjf?.hero || referenceImages.evjf.hero,
      feature: images?.evgEvjf?.feature || '/brand/reference/evjf-feature.webp',
      sidebar: images?.evgEvjf?.sidebar || referenceImages.cards.evjf,
      evgImage: images?.evgEvjf?.evgImage || referenceImages.cards.evg,
      evjfImage: images?.evgEvjf?.evjfImage || referenceImages.cards.evjf,
      planningImages: images?.evgEvjf?.planningImages || [
        '/brand/reference/formules/evjf-hypercube.webp',
        '/brand/reference/formules/evg-laser.webp',
        '/brand/reference/formules/evjf-lightspeed.webp',
        '/brand/reference/formules/evg-wanted.webp',
      ],
    },
    evjf: {
      hero: images?.evjf?.hero || referenceImages.evjf.hero,
      feature: images?.evjf?.feature || '/brand/reference/evjf-feature.webp',
      sidebar: images?.evjf?.sidebar || referenceImages.cards.evjf,
      planningImages: images?.evjf?.planningImages || [
        '/brand/business/gallery-06.webp',
        '/brand/business/gallery-05.webp',
        '/brand/business/gallery-08.webp',
        '/brand/business/gallery-02.webp',
      ],
    },
    evg: {
      hero: images?.evg?.hero || referenceImages.evg.hero,
      feature: images?.evg?.feature || '/brand/reference/evg-feature.webp',
      sidebar: images?.evg?.sidebar || referenceImages.cards.evg,
      planningImages: images?.evg?.planningImages || [
        '/brand/business/gallery-07.webp',
        '/brand/business/gallery-09.webp',
        '/brand/business/gallery-03.webp',
        '/brand/business/gallery-10.webp',
      ],
    },
    anniversaireEnfant: {
      hero: images?.anniversaireEnfant?.hero || referenceImages.anniversaireEnfant.hero,
      feature: images?.anniversaireEnfant?.feature || '/brand/reference/anniversaire-enfant-feature.webp',
      sidebar: images?.anniversaireEnfant?.sidebar || referenceImages.anniversaireEnfant.sidebar,
      planningImages: images?.anniversaireEnfant?.planningImages || [
        '/brand/action-game/aztec-quest-room.webp',
        '/brand/action-game/magic-school-room.webp',
        '/brand/action-game/pirate-bay-room.webp',
        '/brand/action-game/stars-zone-room.webp',
      ],
    },
    anniversaireAdulte: {
      hero: images?.anniversaireAdulte?.hero || referenceImages.anniversaireAdulte.hero,
      feature: images?.anniversaireAdulte?.feature || '/brand/reference/anniversaire-adulte-feature.webp',
      sidebar: images?.anniversaireAdulte?.sidebar || referenceImages.cards.anniversaireAdulte,
      planningImages: images?.anniversaireAdulte?.planningImages || [
        '/brand/action-game/magic-school-room.webp',
        '/brand/action-game/stars-zone-room.webp',
        '/brand/business/gallery-07.webp',
        '/brand/business/gallery-03.webp',
      ],
    },
  };
}

function defaultBrand(brand?: BrandContent) {
  return {
    actionGameLogo: brand?.logos?.actionGame || brandLogos.actionGame,
  };
}

function timelineItem(time: string, label: string, image: string, icon = '', highlight = false): EditableItem {
  return {
    time,
    label,
    image,
    icon,
    highlight: highlight ? 'oui' : '',
  };
}

export function createDefaultPagesContent(images?: ImageContent, brand?: BrandContent): SitePagesContent {
  const img = defaultImages(images);
  const brandData = defaultBrand(brand);

  return {
    home: {
      hero: {
        image: img.home.hero,
        titleLine1: 'GAMESIDE',
        titleLine2: 'CAEN',
        subtitle: "Vous cherchez la meilleure activité à Caen pour vous amuser entre amis, en famille ou entre collègues ? Découvrez Gameside, le complexe immersif à Ifs : 600 m² de salles, 60 minutes pour battre les meilleurs scores. Réservez votre session !",
        primaryCtaLabel: 'RÉSERVER UNE SESSION',
        primaryCtaHref: '/nos-activites/action-game#reservation',
        secondaryCtaLabel: "DÉCOUVRIR L'ACTION GAME",
        secondaryCtaHref: '/nos-activites/action-game',
      },
      activities: {
        title: 'NOS ACTIVITÉS',
        text: "Préparez-vous à vivre une expérience inédite chez Gameside à Caen ! Enchaînez les défis de notre Action Game ou prenez le micro dans notre karaoké privé. À vous de choisir votre activité.",
        actionImage: img.actionGame.intro,
        actionLogo: brandData.actionGameLogo,
        actionTitle: 'Action Game',
        actionText: "Plongez dans l'Action Game : un complexe immersif à parcourir et des défis de 3 minutes à enchaîner en équipe. Notre activité phare, disponible dès l'ouverture !",
        soonItems: [
          { title: 'Nouvelle activité', text: "Un nouveau format inédit se prépare pour compléter votre Gameside à Caen. Restez connectés, l'annonce arrive très bientôt !" },
        ],
      },
      stats: {
        image: img.actionGame.stats,
        items: [
          { value: '3', unit: 'MIN', label: 'PAR SALLE', icon: '/brand/icons/duration-60.svg' },
          { value: '60', unit: 'MIN', label: 'PAR PARTIE', icon: '/brand/icons/duration-60.svg' },
          { value: '+20', unit: '', label: 'SALLES', icon: '/brand/icons/physique.svg' },
          { value: '4', unit: '', label: 'UNIVERS', icon: '/brand/icons/universe.svg' },
          { value: '2 À 5', unit: '', label: 'JOUEURS / ÉQUIPE', icon: '/brand/icons/team-size.svg' },
          { value: '600', unit: 'M²', label: "D'ESPACE IMMERSIF", icon: '/brand/icons/capacity.svg' },
        ],
      },
      formulas: {
        title: 'NOS FORMULES ÉVÉNEMENTS',
        text: "Vous cherchez la meilleure activité à Caen pour un anniversaire, un EVG ou un EVJF ? L'équipe de Gameside vous accueille pour un moment de jeu unique. Le principe ? Formez votre groupe, jouez et explosez les scores !",
        cards: [
          { title: 'EVG / EVJF', text: "Marquez le coup pour un EVG ou un EVJF à Caen avec une activité immersive, fun et simple à organiser pour célébrer les futurs mariés avec tout le groupe.", image: img.evgEvjf.evgImage, secondaryImage: img.evgEvjf.evjfImage, link: '/formules/evg-evjf', cta: 'Préparer la sortie', bannerColor: 'bg-[#1a1b24]' },
          { title: 'Anniversaire Enfant', text: "Offrez à votre enfant un anniversaire à Caen unique en son genre ! Une fête active où les invités courent, rigolent et jouent en équipe. Formule dès 8 ans accompagnés.", image: img.anniversaireEnfant.sidebar, link: '/formules/anniversaire-enfant', cta: "Organiser l'anniversaire", bannerColor: 'bg-yellow-400' },
        ],
      },
      local: {
        poster: img.home.sidebarFeature,
        bgImage: img.actionGame.stats || img.home.hero,
        videoUrl: 'https://www.youtube-nocookie.com/embed/n9Sk-nRVqKI',
        scrollHint: 'Faites défiler pour découvrir',
        title: 'GAMESIDE À CAEN, À IFS',
        paragraphs: [
          "Envie de relever le défi ? Notre complexe vous attend au **350 Rue Paul Boucherot à Ifs** (à 5 minutes de Caen). Le principe est simple : prenez votre bracelet, formez votre équipe et partez à l'aventure !",
          "Avec un rythme effréné et des salles qui changent en permanence, c'est l'activité Caen idéale pour les petits comme pour les très grands groupes. À vous de choisir vos salles et d'élaborer la meilleure stratégie pour gagner.",
        ],
        ctaLabel: 'DEMANDER UN DEVIS',
        ctaHref: '/contact',
      },
      social: {
        title: 'SUIVEZ-NOUS SUR NOS RÉSEAUX SOCIAUX',
        instagramLabel: 'Instagram',
        instagramHref: 'https://www.instagram.com/gameside.caen/',
        facebookLabel: 'Facebook',
        facebookHref: 'https://www.facebook.com/gameside.caen/',
      },
    },
    actionGame: {
      hero: {
        image: img.actionGame.hero,
        titleLine1: "L'ACTION",
        titleLine2: 'GAME',
        subtitle: "Plongez dans l'Action Game Caen : 4 univers immersifs, des défis de 3 minutes chrono et 60 minutes pour battre les meilleurs scores en équipe.",
        primaryCtaLabel: 'Je réserve',
        primaryCtaHref: '/nos-activites/action-game#reservation',
        secondaryCtaLabel: 'Infos pratiques',
        secondaryCtaHref: '/infos-pratiques',
      },
      intro: {
        image: img.actionGame.intro,
        eyebrow: 'Activité immersive à Caen',
        title: 'Une aventure grandeur nature',
        titleAccent: 'en équipe',
        paragraphs: [
          "Préparez-vous à vivre l'Action Game Caen, l'activité immersive nouvelle génération ! Récupérez votre bracelet connecté à l'accueil, formez votre équipe et partez à l'aventure. Pas de game master imposé, pas de scénario figé : c'est vous qui dictez le rythme.",
          "Plongez dans **4 univers spectaculaires** : Aztec Quest (temple aztèque), Magic School (magie), Pirate Bay (pirates) et Stars Zone (futur). Chaque salle propose un défi unique : certaines récompensent la précision, d'autres la rapidité, la mémoire ou la coordination. Avec un format de 3 minutes chrono par épreuve, vous enchaînez les défis sans aucun temps mort.",
          "L'objectif est simple : terminer avec le meilleur score ! Chacun apporte sa pierre à l'édifice. Celui qui vise juste, celle qui repère les détails, le coéquipier qui garde son sang-froid à deux secondes du chrono : tous les profils trouvent leur place, sportifs ou non.",
          "Gameside vous accueille à Ifs, à 5 minutes du centre de Caen, avec un parking gratuit devant le complexe et un accès direct depuis la rocade. **Réservez dès maintenant** votre Action Game à Caen pour vivre une expérience unique en Normandie !",
        ],
      },
      stats: {
        image: img.actionGame.stats,
        items: [
          { value: '3', unit: 'MIN', label: 'PAR SALLE', icon: '/brand/icons/duration-60.svg' },
          { value: '60', unit: 'MIN', label: 'PAR PARTIE', icon: '/brand/icons/duration-60.svg' },
          { value: '+20', unit: '', label: 'SALLES', icon: '/brand/icons/physique.svg' },
          { value: '4', unit: '', label: 'UNIVERS', icon: '/brand/icons/universe.svg' },
          { value: '2 À 5', unit: '', label: 'JOUEURS / ÉQUIPE', icon: '/brand/icons/team-size.svg' },
          { value: '600', unit: 'M²', label: "D'ESPACE IMMERSIF", icon: '/brand/icons/capacity.svg' },
        ],
      },
      reservation: {
        eyebrow: 'Réservation',
        title: 'Réservation',
      },
      howItWorks: {
        eyebrow: 'Comment ça marche',
        title: "Découvrez votre Action Game à Caen en 3 étapes",
        items: [
          { step: '01', title: 'Activez votre bracelet', text: "À votre arrivée, récupérez votre bracelet connecté. Il vous donne accès à toutes les salles et enregistre vos points en temps réel." },
          { step: '02', title: 'Choisissez vos salles', text: "Naviguez librement dans le complexe et choisissez vos défis selon les salles disponibles. Adaptez votre stratégie au fil de la partie pour maximiser vos chances." },
          { step: '03', title: 'Battez le meilleur score', text: "Chaque salle est chronométrée sur 3 minutes. À la fin de la session, votre score d'équipe s'affiche : à vous de viser la première place !" },
        ],
      },
      pricing: {
        title: 'Tarifs Action Game Caen :',
        titleAccent: 'une formule pour chaque joueur',
        text: "Profitez de tarifs adaptés à tous les profils : famille, amis, étudiants ou entreprises. Pour vos anniversaires et événements de groupe, demandez votre devis sur mesure via la page contact.",
        adultImage: img.actionGame.adultCard,
        adultTitle: 'Adultes',
        adultRows: [
          { label: 'Action Game 60 min', price: '25,90 € / pers' },
          { label: 'Action Game 90 min', price: '29,90 € / pers' },
        ],
        juniorImage: img.actionGame.juniorCard,
        juniorTitle: 'Enfants (-16 ans), étudiants & famille*',
        juniorRows: [
          { label: 'Action Game 60 min', price: '25 € / pers' },
          { label: 'Action Game 90 min', price: '24,90 € / pers' },
        ],
        juniorNote: '* à partir de 5 personnes',
      },
      birthdayCta: {
        title: 'Ou choisissez la formule :',
        titleAccent: 'anniversaire à Gameside Caen',
        text: "Envie de fêter un anniversaire à Caen autrement ? Profitez d'une formule clé en main avec devis adapté au nombre de participants, créneau dédié et un espace prévu pour le moment gâteau.",
        ctaLabel: 'Obtenir mon devis personnalisé',
        ctaHref: '/contact',
      },
      universes: {
        eyebrow: 'Les univers',
        title: 'Plus de 20 salles réparties en 4 univers',
        text: "Embarquez dans nos univers spectaculaires : Aztec Quest, Magic School, Pirate Bay et Stars Zone. Chaque univers regroupe plusieurs salles avec leur propre ambiance et leurs propres défis. À vous de choisir votre parcours et de viser le meilleur score !",
        items: actionGameImages.universes.slice(0, 4).map((item) => ({ title: item.title, image: item.image, pictogram: item.pictogram, badge: '3 min' })),
        moreImage: img.actionGame.gallery[21] || img.actionGame.gallery[9] || img.actionGame.stats,
        moreEyebrow: 'Encore plus de salles',
        moreTitle: 'Découvrez bien plus de salles sur place',
        moreText: "Et ce n'est qu'un aperçu ! Bien d'autres salles vous attendent à Gameside Caen pour des sensations toujours renouvelées. À chaque visite, l'expérience est différente : venez vivre l'Action Game Caen et trouvez votre salle préférée !",
      },
      gallery: {
        title: 'Quelques photos des salles',
        text: "Un aperçu des univers de l'Action Game Caen. La meilleure expérience, c'est de venir la vivre directement chez Gameside !",
        images: img.actionGame.gallery,
      },
      eventCta: {
        title: "L'Action Game pour vos événements en groupe",
        text: "Anniversaire, EVG, EVJF, team building ou séminaire : l'Action Game Caen est l'activité idéale pour rassembler votre groupe autour d'un moment unique. Demandez votre devis sur mesure adapté au nombre de participants et au moment de la journée.",
        ctaLabel: 'Mon événement sur mesure',
        ctaHref: '/contact',
      },
      faq: {
        title: "Questions fréquentes sur l'Action Game Caen",
        items: [
          { question: "Qu'est-ce que l'Action Game ?", answer: "L'Action Game à Caen est une activité immersive en équipe sur 600 m². À votre arrivée, vous récupérez un bracelet connecté qui vous donne accès à plus de 20 salles indépendantes. Dans chacune, relevez un défi de 3 minutes avec un score à la clé." },
          { question: "Combien de temps dure une salle ?", answer: "Chaque salle est chronométrée sur 3 minutes en moyenne. Ce format dynamique vous permet d'enchaîner les défis sans temps mort et de découvrir un maximum d'univers durant votre session." },
          { question: "Combien de joueurs par équipe ?", answer: "Le format idéal se joue de 2 à 5 joueurs par équipe. Pour les groupes plus importants, plusieurs équipes peuvent jouer en parallèle et comparer leurs scores à la fin de la session." },
          { question: "Comment réserver ma session d'Action Game à Caen ?", answer: "Pour réserver votre Action Game à Caen, passez par notre [page contact](/contact) ou contactez-nous par téléphone. La réservation en ligne sera disponible très prochainement après l'ouverture." },
          { question: "Proposez-vous des tarifs de groupe ?", answer: "Oui, pour les anniversaires, EVG, EVJF, séminaires ou privatisations, nous proposons des devis sur mesure. Précisez le nombre de participants et le format souhaité via la [page contact](/contact), nous reviendrons avec une proposition adaptée." },
          { question: "Faut-il être sportif pour participer ?", answer: "Pas du tout ! L'Action Game Caen mélange salles physiques (viser, attraper, se déplacer) et salles d'observation, de logique ou de mémoire. Tous les profils peuvent y trouver leur place et briller dans leur équipe." },
          { question: "À partir de quel âge peut-on jouer ?", answer: "L'Action Game est accessible dès 8 ans accompagné d'un adulte, et dès 12 ans en autonomie. Pour un anniversaire enfant, le format s'adapte à l'âge du groupe : précisez-le dans votre demande." },
          { question: "Quelle tenue prévoir ?", answer: "Privilégiez une tenue confortable et des baskets. Vous allez bouger d'une salle à l'autre, parfois vous baisser ou attraper des objets en hauteur. Évitez les talons et les vêtements trop ajustés." },
          { question: "L'Action Game convient-il à un événement de groupe ?", answer: "Absolument ! L'Action Game à Caen est idéal pour vos anniversaires, EVG, EVJF, team building ou séminaires. Consultez nos formules dédiées ou demandez un devis sur mesure via la page contact." },
          { question: "Où se trouve Gameside Caen ?", answer: "Gameside vous accueille au **350 Rue Paul Boucherot, 14123 Ifs**, à seulement 5 minutes du centre de Caen. Parking gratuit devant le complexe, accès direct depuis la rocade." },
        ],
      },
    },
    karaoke: {
      card: {
        image: '/brand/karaoke/karaoke-party-unsplash.webp',
        title: 'Karaoké',
        text: "Prenez le micro, choisissez vos titres et profitez d'une session rien que pour votre groupe dans notre salle de karaoké privée.",
      },
      hero: {
        image: img.karaoke.hero,
        imageAlt: 'Groupe d’amis chantant dans une salle de karaoké',
        title: 'Karaoké Caen',
        subtitle: 'Votre salle privée, vos chansons, votre groupe. La scène est à vous.',
        description: 'Choisissez parmi un large catalogue de titres et partagez une session karaoké rien qu’entre vous chez Gameside Caen.',
        primaryCtaLabel: 'Je réserve',
        primaryCtaHref: '#reservation',
        secondaryCtaLabel: 'Infos pratiques',
        secondaryCtaHref: '/infos-pratiques',
      },
      stats: {
        items: [
          { value: 'Privatif', label: 'Une salle pour votre groupe' },
          { value: 'Vos titres', label: 'Choisissez vos chansons' },
          { value: 'Ensemble', label: 'Entre proches ou collègues' },
          { value: 'À Ifs', label: 'Chez Gameside Caen' },
        ],
      },
      intro: {
        image: '',
        imageAlt: 'La salle de karaoké privée Gameside Caen',
        eyebrow: 'Karaoké privé à Caen',
        title: 'Une salle privée,',
        titleAccent: 'une soirée à votre rythme',
        paragraphs: [
          'À quelques minutes du centre de **Caen**, profitez d’un **karaoké entièrement privatif** chez Gameside à Ifs. Ici, pas de scène face à des inconnus : la salle est réservée uniquement à votre groupe.',
          'Vous choisissez les morceaux, partagez les micros et composez votre soirée à votre rythme. Pour un **anniversaire**, un EVG, un EVJF, une sortie entre amis ou un afterwork, chacun peut chanter sans pression et profiter du moment.',
          'Facilement accessible depuis Caen et son agglomération, cette **salle de karaoké privée** peut aussi compléter une session d’Action Game pour créer une sortie complète au même endroit.',
        ],
      },
      reservation: {
        eyebrow: 'Réservation',
        title: 'Réservez votre karaoké',
      },
      howItWorks: {
        eyebrow: 'Comment ça marche',
        title: 'Votre session en trois étapes',
        items: [
          { step: '01', title: 'Choisissez votre créneau', text: 'Sélectionnez votre horaire et réservez directement en ligne.' },
          { step: '02', title: 'Réunissez votre groupe', text: 'Retrouvez-vous dans une salle privée, réservée uniquement pour vous.' },
          { step: '03', title: 'Prenez le micro', text: 'Choisissez vos morceaux et profitez pleinement de votre session.' },
        ],
      },
      occasions: {
        title: 'Pour toutes vos soirées',
        titleAccent: 'et tous vos événements',
        text: 'Une activité facile à partager, que vous veniez entre amis, en famille ou avec vos collègues.',
        items: [
          { image: '/brand/karaoke/karaoke-party-unsplash.webp', title: 'Entre amis', text: 'Une salle rien que pour votre groupe, vos chansons préférées et votre propre ambiance.' },
          { image: '/brand/karaoke/karaoke-friends-unsplash.webp', title: 'Événements', text: 'Anniversaire, EVJF, EVG ou moment d’équipe : donnez une autre voix à votre sortie.' },
        ],
      },
      gallery: {
        eyebrow: 'Quelques photos d’ambiance',
        title: 'Le karaoké, ça se vit ensemble',
        text: 'Des micros, des refrains repris en chœur et une salle rien que pour votre groupe. Les photos officielles de Gameside Caen seront ajoutées prochainement.',
        images: [],
        ctaLabel: 'Réserver une session',
        ctaHref: '#reservation',
      },
      birthdayCta: {
        image: '/brand/karaoke/karaoke-celebration-pexels.webp',
        imageAlt: 'Groupe d’amis célébrant un anniversaire en chantant au karaoké',
        eyebrow: 'Votre événement',
        title: 'Un anniversaire qui donne',
        titleAccent: 'de la voix',
        paragraphs: [
          'Réunissez vos proches dans une **salle privative près de Caen**, lancez les morceaux que tout le monde connaît et créez une soirée dont votre groupe reparlera.',
          'Le karaoké Gameside à Ifs convient aux **anniversaires adultes et enfants**, aux retrouvailles en famille et aux fêtes entre amis. Votre groupe reste ensemble, dans son propre espace, pendant toute la session.',
        ],
        ctaLabel: 'Réserver une session',
        ctaHref: '#reservation',
      },
      features: {
        title: 'Tout est prêt, à vous de chanter',
        items: [
          { title: 'Une salle 100 % privée', text: "Votre groupe garde la salle et les micros pendant toute la durée du créneau." },
          { title: 'Un large choix de titres', text: "Alternez les nouveautés, les classiques et les morceaux que tout le monde connaît." },
          { title: 'Une soirée à composer', text: "Combinez le karaoké avec l'Action Game ou profitez simplement de votre session de chant." },
          { title: 'Réservation en ligne', text: "Consultez les disponibilités et choisissez votre créneau directement depuis cette page." },
        ],
      },
      faq: {
        title: 'FOIRE AUX QUESTIONS',
        items: [
          { question: 'Le karaoké est-il privatif ?', answer: 'Oui. La salle est réservée à votre groupe pendant toute la durée de votre session.' },
          { question: 'Comment réserver une session ?', answer: 'Choisissez simplement un créneau dans le module de réservation présent sur cette page.' },
          { question: 'Peut-on venir pour un anniversaire ou un EVJF / EVG ?', answer: 'Oui, le karaoké convient aux anniversaires, sorties entre amis, EVJF, EVG et événements d’entreprise.' },
          { question: 'Où se trouve Gameside Caen ?', answer: 'Gameside Caen se trouve à Ifs. Toutes les informations d’accès sont disponibles sur la page **Infos pratiques**.' },
        ],
      },
    },
    nouvelleActivite: {
      hero: {
        image: img.nouvelleActivite.hero,
        title: 'NOUVELLE ACTIVITÉ\nEN PRÉPARATION',
        subtitle: "Une nouvelle activité Caen se prépare chez Gameside ! Un troisième format inédit viendra prochainement compléter votre complexe à Ifs. Restez connectés, l'annonce arrive très bientôt.",
        primaryCtaLabel: 'NOUS CONTACTER',
        primaryCtaHref: '/contact',
      },
      promises: {
        items: [
          { title: "L'esprit Gameside", text: "Une activité Caen pensée pour le groupe, immersive et rythmée, fidèle à l'identité du complexe. Préparez-vous à vivre une nouvelle expérience inédite à Ifs." },
          { title: 'Pensée pour votre soirée', text: "La nouvelle activité Caen complétera l'Action Game et le karaoké pour vous proposer une soirée entière au sein du complexe Gameside." },
          { title: 'Annonce très bientôt', text: "Le nom et le concept seront dévoilés dans les mois suivant l'ouverture. Restez connectés, vous serez les premiers informés !" },
          { title: 'Une expérience exigeante', text: "Comme pour nos autres activités à Caen, immersion, qualité et fun seront au rendez-vous. Une nouvelle aventure à découvrir bientôt chez Gameside Caen." },
        ],
      },
    },
    teamBuilding: createBusinessPageDefaults({
      images: img.teamBuilding,
      eyebrow: 'Team building à Caen',
      heroTitle: 'Team building Caen',
      heroTitleAccent: 'Gameside',
      heroSubtitle: "Organisez un team building Caen premium chez Gameside : une activité immersive clé en main pour renforcer la cohésion de vos équipes à Ifs.",
      watermarkText: 'TEAM',
      introEyebrow: 'Cohésion & challenge',
      introTitle: 'Un team building Caen qui change vraiment la donne',
      paragraphs: [
        "Vous cherchez le meilleur team building à Caen pour rassembler vos équipes ? Préparez-vous à vivre l'Action Game, une expérience immersive qui réunit vos collaborateurs autour d'un vrai défi collectif.",
        "Le principe est simple : formez vos équipes, lancez la session et explorez nos salles thématiques. En 60 minutes, vos collaborateurs vivent une compétition fun et fédératrice où chacun trouve sa place.",
        "Aucun pré-requis sportif n'est nécessaire : l'Action Game fait appel à la logique, l'observation et la coordination tout autant qu'à l'agilité. Les hiérarchies s'effacent, les talents cachés se révèlent et vos équipes repartent avec un vrai souvenir partagé.",
        "Le complexe vous accueille à Ifs, à seulement 5 minutes du centre de Caen, avec un parking gratuit et un accès direct depuis la rocade. Demandez votre devis team building Caen sur mesure dès maintenant !",
      ],
      questionTitle: 'Pourquoi choisir Gameside pour votre team building Caen ?',
      questionText: "Parce qu'une équipe se révèle autrement quand elle relève un vrai défi ensemble. L'Action Game embarque tous vos collaborateurs dans la même aventure : ça communique, ça s'encourage, ça collabore, et chacun repart avec un moment fort à raconter.",
      formatsEyebrow: 'Nos formats team building',
      formatsTitle: 'Trois formats clé en main pour votre team building Caen',
      formatsText: "Profitez d'une formule clé en main adaptée à votre journée d'entreprise. Vivez l'activité seule ou combinez-la avec un temps de travail : nous prenons en charge l'aspect logistique pour que vos équipes profitent pleinement.",
      formats: [
        { title: 'Réunion + activité', text: "Travaillez le matin et lancez le challenge l'après-midi pour casser le rythme d'une journée dense en présentations.", image: img.teamBuilding.gallery[1] || '/brand/business/seminaire-bg.webp' },
        { title: 'Challenge inter-équipes', text: "Plusieurs équipes en parallèle, scores comparés en direct : la formule idéale pour booster la dynamique de groupe.", image: img.teamBuilding.gallery[2] || '/brand/business/gallery-03.webp' },
        { title: 'Cohésion informelle', text: "Un créneau le soir ou en fin de semaine, dédié au renforcement des liens entre collègues hors du cadre professionnel.", image: img.teamBuilding.gallery[3] || '/brand/business/gallery-04.webp' },
      ],
      galleryTitle: 'Vos événements pros en images',
      galleryText: "Renforcez la cohésion de vos équipes et créez des souvenirs inoubliables. Parcourez notre galerie pour découvrir comment nos entreprises partenaires vivent l'expérience et préparez votre futur team building Caen !",
      faqTitle: 'Questions fréquentes sur le Team Building',
      faqItems: [
        { question: 'Comment obtenir un devis ?', answer: "Toutes les demandes team building sont sur devis. Passez par la [page contact](/contact) avec la date envisagée, l'effectif et le format souhaité. On revient rapidement avec une proposition adaptée." },
        { question: 'Il faut être sportif ?', answer: "Pas du tout. Une bonne moitié des salles se gagnent par la logique, l'observation ou la mémoire. Le collègue qui ne fait jamais de sport peut très bien finir top scorer." },
        { question: 'On vient à combien ?', answer: "Ça marche dès une dizaine de personnes. Au-delà, on monte plusieurs équipes qui tournent en parallèle. Indiquez l'effectif dans votre demande de devis, on vous propose le format adapté." },
        { question: 'On peut faire les équipes comme on veut ?', answer: "Oui. Par service, au hasard, managers contre terrain, ou avec le futur champion autoproclamé dans chaque camp. C'est vous qui décidez." },
        { question: "Ça prend combien de temps ?", answer: "Comptez 1h30 sur place pour une session classique : accueil, briefing, activité, debriefing rapide. Pour un format plus long avec réunion incluse, nous adaptons la formule lors du devis." },
        { question: "Le manager joue avec l'équipe ?", answer: "Oui, et c'est ce qui rend le moment mémorable. Les hiérarchies s'effacent vite quand tout le monde est dans la même salle avec un chrono." },
        { question: "Peut-on récupérer les scores pour un classement interne ?", answer: "Oui. En fin de session, les scores par équipe s'affichent. Si vous voulez un palmarès formel ou un petit trophée symbolique pour les gagnants, dites-le en amont, nous nous adaptons." },
        { question: "À partir de quel effectif minimum ?", answer: "Le format team building démarre à partir d'une dizaine de participants. En dessous, on bascule sur du tarif individuel ou un format événement privé selon votre besoin." },
        { question: "Peut-on combiner avec un repas ou un cocktail ?", answer: "On a l'habitude d'accueillir des groupes qui prévoient un moment convivial après l'activité. Précisez-le dans votre demande de devis, nous adaptons la formule selon votre programme." },
      ],
      timelineItems: [
        timelineItem('09h00', 'Accueil & briefing', getOrderedTimelinePictogram(0), 'Handshake'),
        timelineItem('09h15', 'Remise des bracelets', getOrderedTimelinePictogram(1), 'Watch'),
        timelineItem('09h30', 'Session Action Game', getOrderedTimelinePictogram(2), 'Gamepad2', true),
        timelineItem('10h30', 'Résultats & scores', getOrderedTimelinePictogram(3), 'Trophy'),
        timelineItem('11h00', 'Debriefing équipe', getOrderedTimelinePictogram(4), 'UsersRound'),
        timelineItem('12h00+', 'Suite du programme', getOrderedTimelinePictogram(5), 'PartyPopper'),
      ],
    }),
    privatisation: createBusinessPageDefaults({
      images: img.privatisation,
      eyebrow: 'Privatisation à Caen',
      heroTitle: 'Privatisation salle Caen',
      heroTitleAccent: 'Gameside',
      heroSubtitle: "Privatisez le complexe Gameside à Ifs pour votre soirée client, lancement de produit, anniversaire ou événement associatif. Un lieu immersif et clé en main pour marquer les esprits.",
      watermarkText: 'PRIVÉ',
      introEyebrow: 'Lieu premium',
      introTitle: 'Un lieu unique pour votre privatisation Caen',
      paragraphs: [
        "Vous cherchez un lieu de privatisation à Caen qui sorte de l'ordinaire ? Découvrez Gameside, un complexe immersif clé en main où l'expérience démarre dès l'arrivée de vos invités.",
        "Préparez-vous à offrir à vos convives un événement inoubliable : un complexe entièrement à votre disposition, un univers immersif et une activité qui brise la glace en quelques minutes. Vos invités arrivent, forment des équipes et plongent dans une expérience unique en Normandie.",
        "Notre formule de privatisation Caen s'adapte à tous vos projets : soirée client, lancement de produit, événement associatif, anniversaire d'envergure ou rassemblement professionnel. Un cadre premium, mémorable, dont vos invités parleront encore des semaines plus tard.",
        "Gameside vous accueille à Ifs, à 5 minutes de Caen avec un grand parking gratuit. Demandez votre devis privatisation Caen dès aujourd'hui via notre [page contact](/contact). Nous prenons en charge l'aspect logistique pour que vous profitiez pleinement de votre événement.",
      ],
      questionTitle: 'Pourquoi privatiser Gameside Caen ?',
      questionText: "Offrez à vos invités une expérience clé en main et inoubliable. L'Action Game donne immédiatement un fil rouge à votre soirée : vos convives ont une activité fédératrice, des équipes formées et un score commun à atteindre. Vous gérez l'humain, nous gérons le contenu.",
      formatsEyebrow: 'Nos formats',
      formatsTitle: 'Trois manières de privatiser votre événement à Caen',
      formatsText: "Profitez d'une privatisation totale, partielle ou d'un créneau dédié selon la taille de votre groupe et le moment de la journée. Demandez votre devis privatisation Caen sur mesure pour caler ensemble votre événement idéal.",
      formats: [
        { title: 'Soirée client', text: "Recevez vos clients, partenaires ou prospects dans un cadre qui les marquera durablement. Une alternative premium au cocktail dînatoire classique.", image: img.privatisation.gallery[0] || '/brand/business/gallery-01.webp' },
        { title: 'Lancement / annonce', text: "Lancez un produit, annoncez un projet ou marquez un cap dans un lieu mémorable. L'activité fournit le contenu de la soirée, vous concentrez l'énergie sur votre prise de parole.", image: img.privatisation.gallery[1] || '/brand/business/gallery-04.webp' },
        { title: 'Groupe exclusif', text: "Profitez d'un créneau dédié à votre groupe : aucun autre client en parallèle, accès complet à toutes les salles. Le complexe Gameside Caen est entièrement à vous.", image: img.privatisation.gallery[2] || '/brand/business/gallery-10.webp' },
      ],
      galleryTitle: 'Vos événements pros en images',
      galleryText: "Renforcez la cohésion de vos équipes et créez des souvenirs inoubliables. Parcourez notre galerie pour découvrir comment nos entreprises partenaires vivent l'expérience et préparez votre future privatisation Caen !",
      faqTitle: 'Questions fréquentes sur la privatisation',
      faqItems: [
        { question: 'Comment obtenir un devis ?', answer: "Toutes les privatisations sont sur devis. Passez par la [page contact](/contact) avec la date, le nombre d'invités et le type d'événement. On revient rapidement avec une proposition." },
        { question: 'On peut privatiser une partie seulement ?', answer: "Oui. La privatisation peut être partielle ou totale selon la taille du groupe et le créneau. On en discute lors du devis selon votre besoin." },
        { question: "Ça marche pour quels types d'événements ?", answer: "Soirée client, lancement de produit, anniversaire d'envergure, événement associatif, sortie de groupe d'amis... Dès qu'il y a un groupe à réunir autour d'une activité commune, le format tient." },
        { question: "Tous les invités doivent jouer ?", answer: "Non. Ceux qui veulent jouent et forment des équipes ; les autres peuvent suivre l'ambiance, regarder ou échanger autour. L'Action Game embarque facilement, mais rien n'est obligatoire." },
        { question: "Combien de personnes peut-on accueillir au maximum ?", answer: "Le complexe tourne bien jusqu'à une centaine de personnes en privatisation totale. Au-delà, on en discute selon la formule. Plusieurs sessions peuvent s'enchaîner selon votre programme." },
        { question: "Peut-on apporter ses propres boissons ou traiteur ?", answer: "Selon le type d'événement, c'est envisageable. Précisez-le dans votre demande de devis, nous adaptons la formule." },
        { question: "Jusqu'à quelle heure peut-on prolonger la soirée ?", answer: "Les créneaux de privatisation s'adaptent à votre programme : soirée jusqu'à 22h-23h ou prolongation possible selon le format. À voir lors du devis." },
        { question: "Le parking est-il accessible aux invités ?", answer: "Oui, parking gratuit à proximité immédiate du complexe. À 5 minutes du centre de Caen, accessible depuis la rocade." },
      ],
      timelineItems: [
        timelineItem('18h00', 'Accueil des invités', getOrderedTimelinePictogram(0), 'Handshake'),
        timelineItem('18h15', 'Remise des bracelets', getOrderedTimelinePictogram(1), 'Watch'),
        timelineItem('19h00', 'Session Action Game', getOrderedTimelinePictogram(2), 'Gamepad2', true),
        timelineItem('20h00', 'Résultats & scores', getOrderedTimelinePictogram(3), 'Trophy'),
        timelineItem('20h30', 'Temps convivial', getOrderedTimelinePictogram(4), 'Utensils'),
        timelineItem('22h00+', 'Fin de soirée', getOrderedTimelinePictogram(5), 'Sparkles'),
      ],
    }),
    seminaire: createBusinessPageDefaults({
      images: img.seminaire,
      eyebrow: 'Séminaire à Caen',
      heroTitle: 'Séminaire Caen',
      heroTitleAccent: 'Gameside',
      heroSubtitle: "Organisez votre séminaire Caen chez Gameside : un environnement immersif et clé en main à Ifs pour combiner réunions, cohésion d'équipe et activités fédératrices.",
      watermarkText: 'SÉMINAIRE',
      introEyebrow: 'Travail & cohésion',
      introTitle: 'Un séminaire Caen qui sort vraiment du cadre',
      paragraphs: [
        "Vous cherchez le meilleur lieu pour organiser votre séminaire à Caen ? Préparez-vous à proposer à vos collaborateurs un séminaire premium qui combine temps de travail et activité fédératrice, dans un cadre immersif unique en Normandie.",
        "Le principe de notre séminaire Caen ? Alternez réunions et Action Game pour donner du rythme à votre journée. Vos équipes travaillent, échangent, puis basculent dans une activité collective qui ravive l'énergie du groupe.",
        "Demi-journée, journée complète, séminaire de rentrée ou de cohésion : nos formats s'adaptent à votre programme. Vous gardez la main sur votre contenu pro, nous prenons en charge l'aspect activité et logistique.",
        "Gameside vous accueille à Ifs, à 5 minutes de Caen, avec un parking gratuit. Demandez votre devis séminaire Caen sur mesure dès maintenant via notre [page contact](/contact) !",
      ],
      questionTitle: 'Pourquoi choisir Gameside pour votre séminaire Caen ?',
      questionText: "Offrez à vos collaborateurs un séminaire qui fait la différence. Après vos temps de réunion, vos équipes plongent dans une activité immersive qui crée des souvenirs partagés et renforce naturellement la cohésion. Une expérience clé en main, mémorable, à 5 minutes de Caen.",
      formatsEyebrow: 'Nos formats',
      formatsTitle: 'Trois formats clé en main pour votre séminaire à Caen',
      formatsText: "Profitez d'une formule clé en main qui combine travail et activité dans un même lieu. Demandez votre devis séminaire Caen sur mesure pour un programme adapté à votre journée.",
      formats: [
        { title: 'Demi-journée', text: "Une réunion compacte le matin, puis une session Action Game l'après-midi : le format idéal pour relancer la dynamique et finir sur une note collective.", image: img.seminaire.gallery[1] || '/brand/business/seminaire-bg.webp' },
        { title: 'Atelier + jeu', text: "Un atelier de travail le matin, suivi d'une session Action Game pour remettre vos équipes dans l'énergie collective sans casser le rythme.", image: img.seminaire.gallery[2] || '/brand/business/gallery-02.webp' },
        { title: 'Journée cohésion', text: "Une journée entièrement dédiée au collectif, avec défis, scores et moments d'échange. Idéal pour intégrer une nouvelle équipe ou souder un groupe.", image: img.seminaire.gallery[3] || '/brand/business/gallery-07.webp' },
      ],
      galleryTitle: 'Vos événements pros en images',
      galleryText: "Renforcez la cohésion de vos équipes et créez des souvenirs inoubliables. Parcourez notre galerie pour découvrir comment nos entreprises partenaires vivent l'expérience et préparez votre futur séminaire Caen !",
      faqTitle: 'Questions fréquentes sur le séminaire',
      faqItems: [
        { question: 'Comment obtenir un devis ?', answer: "Tous les séminaires sont sur devis. Passez par la [page contact](/contact) avec la date, l'effectif, le temps de réunion souhaité et le moment prévu pour l'activité. On revient avec une proposition adaptée." },
        { question: "Peut-on prévoir un temps de réunion avant l'activité ?", answer: "Oui, c'est même le format le plus demandé. On peut caler du temps de travail avant ou après l'Action Game selon votre programme." },
        { question: "L'Action Game dure combien de temps ?", answer: "Une session classique fait 60 minutes. Avec accueil, briefing et debriefing rapide, comptez plutôt 1h30 sur place pour la partie activité." },
        { question: "C'est adapté à une équipe qui se connaît peu ?", answer: "Très bien adapté. Les défis font tomber les barrières en quelques minutes : on communique, on s'entraide, et les rôles se créent tout seuls." },
        { question: "Y a-t-il un espace disponible pour tenir une réunion sur place ?", answer: "Selon le créneau et la taille du groupe, on peut trouver une configuration qui combine les deux dans le même lieu. À voir lors du devis." },
        { question: "C'est quoi l'avantage par rapport à un séminaire classique ?", answer: "L'activité est incluse dans le lieu. Pas besoin d'organiser un transfert ou une prestation externe. Vos équipes travaillent, jouent ensemble, et repartent avec un vrai souvenir. Le tout à 5 minutes de Caen, parking gratuit." },
        { question: "À partir de quel effectif minimum ?", answer: "Le format séminaire démarre à partir d'une dizaine de participants. Pour un effectif plus restreint, on adapte la proposition lors du devis." },
        { question: "Peut-on prévoir un déjeuner ou un cocktail ?", answer: "On a l'habitude d'accueillir des séminaires qui intègrent un repas ou un cocktail. Précisez-le dans votre demande de devis pour qu'on prévoie le créneau et la formule." },
      ],
      timelineItems: [
        timelineItem('09h00', 'Accueil & café', getOrderedTimelinePictogram(0), 'Coffee'),
        timelineItem('09h30', 'Temps de travail', getOrderedTimelinePictogram(1), 'Presentation'),
        timelineItem('12h00', 'Déjeuner', getOrderedTimelinePictogram(2), 'UtensilsCrossed'),
        timelineItem('14h00', 'Session Action Game', getOrderedTimelinePictogram(3), 'Gamepad2', true),
        timelineItem('15h00', 'Résultats & scores', getOrderedTimelinePictogram(4), 'Trophy'),
        timelineItem('16h00', 'Clôture', getOrderedTimelinePictogram(5), 'Flag'),
      ],
    }),
    evgEvjf: createFormulaPageDefaults({
      images: {
        ...img.evgEvjf,
        hero: img.evgEvjf.sidebar,
        feature: img.evgEvjf.sidebar,
        planningImages: [
          '/brand/reference/formules/evjf-hypercube.webp',
          '/brand/reference/formules/evg-laser.webp',
          '/brand/reference/formules/evjf-lightspeed.webp',
          '/brand/reference/formules/evg-wanted.webp',
        ],
      },
      heroTitle: 'EVG / EVJF Caen\nentre amis',
      subtitle: "Organisez un EVG ou un EVJF à Caen sans vous compliquer la journée : une activité immersive, fun et fédératrice pour célébrer les futurs mariés à Ifs, à 5 minutes du centre de Caen.",
      watermark: 'EVG EVJF',
      introTitle: 'ENTERREMENT DE\nVIE DE CÉLIBATAIRE',
      introBadge: 'SUR DEVIS',
      introLead: 'Préparez un EVG ou EVJF Caen vraiment unique',
      introParagraphs: [
        "Vous cherchez la meilleure activité pour un EVG ou un EVJF à Caen ? L'équipe de Gameside vous accueille pour un moment de jeu unique entre amis. Le principe ? Formez votre groupe, plongez dans nos univers immersifs et explosez les scores !",
        "Préparez-vous à vivre une expérience inédite : pas besoin d'être sportif ou stratège, l'Action Game fait appel à l'adresse, l'observation, la mémoire et la coordination. Chaque personne trouve son rôle et les futurs mariés se retrouvent naturellement au cœur des défis.",
        "Profitez d'une formule clé en main pour votre EVG ou EVJF Caen : pas de programme à improviser, pas d'animation à organiser. Vous arrivez, vous formez les équipes et vous démarrez la journée dans un cadre immersif unique en Normandie.",
        "Gameside vous accueille à Ifs, à 5 minutes du centre de Caen, avec un parking gratuit. Demandez votre devis EVG / EVJF Caen sur mesure dès maintenant via notre [page contact](/contact) !",
      ],
      planningEyebrow: 'Le moment',
      planningTitle: 'Un EVG / EVJF Caen rythmé et clé en main',
      planningText: "Profitez d'une formule sans prise de tête : à votre arrivée, récupérez vos bracelets, formez les équipes et partez à l'aventure. La session dure 60 minutes, le temps file et tout le groupe repart avec des anecdotes à raconter.",
      planningItems: [
        { title: 'Du challenge', text: "Des salles aux compétences variées : viser, mémoriser, observer, coordonner. De quoi tester tout le monde, futurs mariés en tête." },
        { title: 'Du fun', text: "Une activité qui embarque le groupe en quelques minutes, parfaite pour mélanger amis proches, témoins et nouveaux venus dans la même équipe." },
        { title: 'Un bon moment', text: "Un format simple à organiser qui rassemble tout le monde autour d'une activité commune avant de poursuivre la journée ou la soirée." },
      ],
      timelineItems: [
        timelineItem('14h00', 'Accueil du groupe', getOrderedTimelinePictogram(0), 'Users'),
        timelineItem('14h15', 'Remise des bracelets', getOrderedTimelinePictogram(1), 'Watch'),
        timelineItem('14h15', 'Session Action Game', getOrderedTimelinePictogram(2), 'Gamepad2', true),
        timelineItem('15h15', 'Résultats & scores', getOrderedTimelinePictogram(3), 'Trophy'),
        timelineItem('15h30', 'Photos & souvenirs', getOrderedTimelinePictogram(4), 'Camera'),
        timelineItem('16h00+', 'Suite de la journée', getOrderedTimelinePictogram(5), 'Sparkles'),
      ],
      selectionTitle: 'CE QUE LA FORMULE INCLUT',
      faqTitle: "Questions fréquentes sur les EVG / EVJF",
      faqItems: [
        { question: 'On vient à combien ?', answer: "À partir de 5 et jusqu'à des groupes plus larges. Au-delà de 10, on monte plusieurs équipes qui jouent en parallèle et comparent leurs scores. Donnez-nous l'effectif, nous nous adaptons." },
        { question: 'On peut venir déguisés ?', answer: "Bien sûr. Le seul critère, c'est de pouvoir bouger librement. Donc baskets et tenue qui ne gêne pas, sinon vous serez moins à l'aise dans certaines salles." },
        { question: 'Faut-il être sportif ?', answer: "Non. Certaines salles demandent un peu de mouvement, d'autres reposent sur l'observation, la mémoire ou la logique. Tout le monde s'amuse, peu importe son niveau." },
        { question: 'On prévoit combien de temps sur place ?', answer: "Au moins 1h30 : le temps d'arriver, de jouer la session de 60 minutes et de comparer les scores avant de partir sur la suite." },
        { question: "On peut prévoir des défis pour la future mariée ou le futur marié ?", answer: "Oui, vous avez toute la liberté. Duel témoins, objectif meilleur score, gage à la moindre erreur... Vous décidez, nous nous adaptons." },
        { question: "Comment demander un devis ?", answer: "Via la page contact, indiquez la date, le nombre de participants et le profil du groupe. On revient vite avec une proposition adaptée." },
      ],
    }),
    evjf: createFormulaPageDefaults({
      images: {
        ...img.evjf,
        hero: img.evjf.sidebar,
        feature: img.evjf.sidebar,
        planningImages: [
          '/brand/reference/formules/evjf-hypercube.webp',
          '/brand/reference/formules/evjf-lightspeed.webp',
          '/brand/reference/formules/evjf-botanic.webp',
          '/brand/reference/formules/evjf-scarabee.webp',
        ],
      },
      heroTitle: 'EVJF Caen\nentre amies',
      subtitle: "Faites de cet EVJF Caen un moment inoubliable ! Une activité fun et immersive pour célébrer la future mariée entre amies, à Ifs, à 5 minutes du centre de Caen.",
      watermark: 'EVJF',
      introTitle: 'ENTERREMENT DE\nVIE DE JEUNE FILLE',
      introBadge: 'SUR DEVIS',
      introLead: 'Préparez un EVJF Caen vraiment unique',
      introParagraphs: [
        "Vous cherchez la meilleure activité pour un EVJF à Caen ? L'équipe de Gameside vous accueille pour un moment de jeu unique entre amies. Le principe ? Formez votre groupe, plongez dans nos univers immersifs et explosez les scores !",
        "Préparez-vous à vivre une expérience inédite : pas besoin d'être sportive ou stratège, l'Action Game fait appel à l'adresse, l'observation, la mémoire et la coordination. Chacune trouve son rôle et la future mariée se retrouve naturellement au cœur des défis.",
        "Profitez d'une formule clé en main pour cet EVJF Caen : pas de programme à organiser, pas d'animation à improviser. Vous venez, vous jouez, vous riez ensemble. Le tout dans un cadre immersif unique en Normandie.",
        "Gameside vous accueille à Ifs, à 5 minutes du centre de Caen, avec un parking gratuit. Demandez votre devis EVJF Caen sur mesure dès maintenant via notre [page contact](/contact) !",
      ],
      planningEyebrow: 'Le moment',
      planningTitle: 'Un EVJF Caen rythmé et clé en main',
      planningText: "Profitez d'une formule sans prise de tête : à votre arrivée, récupérez vos bracelets, formez votre équipe et partez à l'aventure. La session dure 60 minutes, le temps file et personne ne s'ennuie. L'EVJF parfait quand vous voulez du fun garanti !",
      planningItems: [
        { title: 'Du fun', text: "Découvrez nos salles colorées et immersives qui plaisent à toutes : celles qui adorent l'action comme celles qui préfèrent la réflexion." },
        { title: 'Du challenge', text: "Vivez une vraie compétition entre équipes avec des scores affichés en temps réel. De quoi prolonger la soirée en chambrant la future mariée !" },
        { title: 'Un bon moment', text: "Profitez d'une formule clé en main qui rassemble tout le groupe autour d'une activité commune dès l'arrivée." },
      ],
      timelineItems: [
        timelineItem('14h00', 'Accueil du groupe', getOrderedTimelinePictogram(0), 'Users'),
        timelineItem('14h15', 'Remise des bracelets', getOrderedTimelinePictogram(1), 'Watch'),
        timelineItem('14h15', 'Session Action Game', getOrderedTimelinePictogram(2), 'Gamepad2', true),
        timelineItem('15h15', 'Résultats & scores', getOrderedTimelinePictogram(3), 'Trophy'),
        timelineItem('15h30', 'Photos & souvenirs', getOrderedTimelinePictogram(4), 'Camera'),
        timelineItem('16h30+', 'Suite de la soirée', getOrderedTimelinePictogram(5), 'Sparkles'),
      ],
      selectionTitle: 'CE QUE LA FORMULE INCLUT',
      faqTitle: "Questions fréquentes sur l'EVJF",
      faqItems: [
        { question: 'On vient à combien ?', answer: "À partir de 5 et jusqu'à des groupes plus larges. Au-delà de 10, on monte plusieurs équipes qui jouent en parallèle et comparent leurs scores. Donnez-nous l'effectif, nous nous adaptons." },
        { question: 'On peut venir déguisées ?', answer: "Bien sûr. Le seul critère, c'est de pouvoir bouger librement. Donc baskets et tenue qui ne gêne pas, sinon vous serez moins à l'aise dans certaines salles." },
        { question: "Faut-il être sportive ?", answer: "Non. Certaines salles demandent un peu de mouvement, d'autres reposent sur l'observation, la mémoire ou la logique. Tout le monde s'amuse, peu importe son niveau." },
        { question: 'On prévoit combien de temps sur place ?', answer: "Au moins 1h30 : le temps d'arriver, de jouer la session de 60 minutes et de discuter un peu avant de filer sur la suite de la journée." },
        { question: "Comment on s'organise pour le devis ?", answer: "Page contact, vous nous donnez la date, le nombre de participantes et un peu de contexte sur le groupe. On revient vite avec une proposition." },
      ],
    }),
    evg: createFormulaPageDefaults({
      images: {
        ...img.evg,
        hero: img.evg.sidebar,
        feature: img.evg.sidebar,
        planningImages: [
          '/brand/reference/formules/evg-laser.webp',
          '/brand/reference/formules/evg-wanted.webp',
          '/brand/reference/formules/evg-couloir.webp',
          '/brand/reference/formules/evg-sphynx.webp',
        ],
      },
      heroTitle: 'EVG Caen\nentre amis',
      subtitle: "Marquez le coup pour cet EVG Caen ! Une activité immersive et fédératrice pour célébrer le futur marié entre potes, à Ifs, à 5 minutes du centre de Caen.",
      watermark: 'EVG',
      introTitle: 'ENTERREMENT DE\nVIE DE GARÇON',
      introBadge: 'SUR DEVIS',
      introLead: 'Préparez un EVG Caen vraiment unique',
      introParagraphs: [
        "Vous cherchez la meilleure activité pour un EVG à Caen ? L'équipe de Gameside vous accueille pour un moment de jeu unique entre potes. Le principe ? Formez votre groupe, plongez dans nos univers immersifs et explosez les scores !",
        "Préparez-vous à vivre une expérience inédite : pas besoin d'être sportif, l'Action Game fait appel à l'adresse, l'observation, la mémoire et la coordination. Chacun trouve son rôle et le futur marié se retrouve naturellement au cœur des défis pour des moments inoubliables.",
        "Profitez d'une formule clé en main pour cet EVG Caen : pas de programme à improviser, pas d'animation à organiser. Vous arrivez, vous formez vos équipes et vous démarrez la journée du bon pied dans un cadre immersif unique en Normandie.",
        "Gameside vous accueille à Ifs, à 5 minutes du centre de Caen, avec un parking gratuit. Demandez votre devis EVG Caen sur mesure dès maintenant via notre [page contact](/contact) !",
      ],
      planningEyebrow: 'Le moment',
      planningTitle: 'Un EVG Caen rythmé et clé en main',
      planningText: "Profitez d'une formule clé en main : à votre arrivée, récupérez vos bracelets, formez vos équipes et lancez la session. À la fin, comparez les scores et désignez les héros du jour. Une organisation simple pour un EVG mémorable !",
      planningItems: [
        { title: 'Du challenge', text: "Des salles aux compétences variées : viser, mémoriser, observer, coordonner. De quoi tester tout le monde, futur marié en tête." },
        { title: 'Du fun', text: "Une activité qui embarque le groupe en quelques minutes, parfaite pour mélanger amis proches et nouveaux venus dans la même équipe." },
        { title: 'Un bon moment', text: "Un vrai moment partagé entre potes, avec des anecdotes à raconter pendant des mois après le mariage." },
      ],
      timelineItems: [
        timelineItem('14h00', 'Accueil du groupe', getOrderedTimelinePictogram(0), 'Users'),
        timelineItem('14h15', 'Remise des bracelets', getOrderedTimelinePictogram(1), 'Watch'),
        timelineItem('14h15', 'Session Action Game', getOrderedTimelinePictogram(2), 'Gamepad2', true),
        timelineItem('15h15', 'Résultats & scores', getOrderedTimelinePictogram(3), 'Trophy'),
        timelineItem('15h30', 'Chambre le marié', getOrderedTimelinePictogram(4), 'Camera'),
        timelineItem('16h00+', 'Suite de la soirée', getOrderedTimelinePictogram(5), 'Sparkles'),
      ],
      selectionTitle: 'CE QUE LA FORMULE INCLUT',
      faqTitle: "Questions fréquentes sur l'EVG",
      faqItems: [
        { question: 'On peut venir déguisés ?', answer: "Aucun souci, tant que la tenue ne vous empêche pas de bouger. Les baskets sont vivement conseillées." },
        { question: 'Faut-il être sportif ?', answer: "Non. Les salles mélangent action, logique, observation et mémoire. Le copain le moins sportif peut très bien finir top scorer." },
        { question: 'Il faut combien de temps sur place ?', answer: "Comptez 1h30 minimum : le temps d'arriver, de jouer la session de 60 minutes et de comparer les scores avant de partir sur la suite." },
        { question: "On peut imposer des défis spéciaux au futur marié ?", answer: "Oui, vous avez toute la liberté. Duel témoins / futur marié, gage à la moindre erreur, objectif meilleur score imposé... Vous décidez, nous nous adaptons." },
        { question: 'Comment demander un devis ?', answer: "Via la page contact, indiquez la date, le nombre de participants et le profil du groupe. On revient vite avec une proposition adaptée." },
      ],
    }),
    anniversaireEnfant: createFormulaPageDefaults({
      images: {
        ...img.anniversaireEnfant,
        hero: img.anniversaireEnfant.sidebar,
        feature: img.anniversaireEnfant.sidebar,
        planningImages: [
          '/brand/reference/formules/anniversaire-enfant-simon-run.webp',
          '/brand/reference/formules/anniversaire-enfant-simon-run-close.webp',
          '/brand/reference/formules/anniversaire-enfant-crocodiles.webp',
          '/brand/reference/formules/anniversaire-enfant-spectres.webp',
        ],
      },
      heroTitle: 'Anniversaire enfant\nCaen',
      subtitle: "Offrez à votre enfant un anniversaire à Caen unique en son genre ! Une fête active et immersive chez Gameside, à Ifs, à 5 minutes du centre. Formule clé en main dès 8 ans.",
      watermark: 'ENFANT',
      introTitle: 'UNE FÊTE\nINOUBLIABLE',
      introBadge: 'SUR DEVIS',
      introLead: 'Préparez un anniversaire enfant Caen vraiment unique',
      introParagraphs: [
        "Vous cherchez la meilleure activité pour un anniversaire enfant à Caen ? L'équipe de Gameside vous accueille pour un moment de jeu unique avec votre enfant et ses invités. Le principe ? Formez les équipes, plongez dans nos univers immersifs et explosez les scores !",
        "Préparez-vous à offrir un anniversaire mémorable : nos salles aux univers spectaculaires (pirates, temple aztèque, magie, futur) plongent les enfants dans une vraie aventure. Ils courent, observent, coopèrent et relèvent des défis chronométrés en équipe.",
        "Profitez d'une formule clé en main pour cet anniversaire enfant Caen : pas d'animation à improviser, pas de jeux à organiser. Vos invités arrivent, vivent l'activité, repartent avec des étoiles plein les yeux. Un créneau dédié est prévu pour le gâteau et les cadeaux.",
        "Gameside vous accueille à Ifs, à 5 minutes du centre de Caen, avec un parking gratuit. Demandez votre devis anniversaire enfant Caen sur mesure dès maintenant via notre [page contact](/contact) !",
      ],
      planningEyebrow: 'Pendant la fête',
      planningTitle: 'Un anniversaire enfant Caen clé en main',
      planningText: "Profitez d'une formule simple à organiser : on accueille le groupe, on remet les bracelets et c'est parti pour 60 minutes de jeu. Un espace dédié est prévu pour le gâteau et les cadeaux après l'activité.",
      planningItems: [
        { title: 'Du fun', text: "Des salles aux univers immersifs et colorés qui plaisent aux enfants : pirates, magie, futur..." },
        { title: 'Du challenge', text: "Des défis variés et adaptés : viser, mémoriser, observer. Chaque enfant trouve sa salle préférée et brille à son tour." },
        { title: 'Un bon moment', text: "Une activité qui réunit le groupe et fait passer le temps incroyablement vite, sans avoir à relancer l'ambiance." },
      ],
      timelineItems: [
        timelineItem('14h00', 'Accueil des enfants', getOrderedTimelinePictogram(0), 'Users'),
        timelineItem('14h15', 'Remise des bracelets', getOrderedTimelinePictogram(1), 'Watch'),
        timelineItem('14h15', 'Session Action Game', getOrderedTimelinePictogram(2), 'Gamepad2', true),
        timelineItem('15h15', 'Résultats & scores', getOrderedTimelinePictogram(3), 'Trophy'),
        timelineItem('15h30', 'Gâteau & cadeaux', getOrderedTimelinePictogram(4), 'Cake'),
        timelineItem('16h30', 'Fin de la fête', getOrderedTimelinePictogram(5), 'Sparkles'),
      ],
      selectionTitle: 'CE QUE LA FORMULE INCLUT',
      faqTitle: "Questions sur l'anniversaire enfants",
      faqItems: [
        { question: "À partir de quel âge ?", answer: "Dès 8 ans accompagnés d'un adulte, dès 12 ans en autonomie. Pour les plus jeunes, on peut discuter ensemble pour adapter le format." },
        { question: "On vient à combien minimum ?", answer: "Les formules anniversaire démarrent à partir de 5 enfants. En dessous, ça reste possible mais on passe sur du tarif individuel." },
        { question: "Un parent doit accompagner ?", answer: "Oui pour les moins de 12 ans : un adulte accompagnateur doit être présent. Pas besoin d'être un par enfant, un ou deux suffisent généralement." },
        { question: 'Comment les habiller ?', answer: "Tenue confortable et baskets. Ils vont bouger et se baisser parfois, donc évitez les vêtements de cérémonie et les sandales." },
        { question: 'On peut faire le gâteau et les cadeaux sur place ?', answer: "Oui, on a un coin pour ça. Précisez-le dans votre demande pour qu'on prévoie le créneau après l'activité." },
      ],
    }),
    infosPratiques: {
      hero: {
        image: img.infosPratiques.hero,
        title: 'INFOS PRATIQUES GAMESIDE CAEN',
        subtitle: 'Tout ce qu\'il faut savoir avant de venir : adresse, horaires, accès, tarifs et réponses aux questions qu\'on nous pose le plus.',
      },
      faq: {
        title: 'FOIRE AUX QUESTIONS',
        subtitle: "Si la réponse n'est pas ici, écrivez-nous. On rajoutera la question.",
        ctaLabel: 'Contacter Gameside',
        ctaHref: '/contact',
        items: [
          { question: "C'est quoi Gameside ?", answer: "Un complexe de loisirs qui ouvre à Ifs, à 5 minutes de Caen. On démarre avec l'Action Game (notre activité phare), et on ajoutera ensuite un karaoké privé puis une troisième activité." },
          { question: "On peut organiser un anniversaire ?", answer: "Oui. Pour les enfants comme pour les adultes. On a des formules pensées pour garder le groupe ensemble avant, pendant et après l'activité, avec un peu de souplesse pour le moment gâteau." },
          { question: "Et un événement d'entreprise ?", answer: "Oui : team building, séminaire, soirée client, privatisation. On adapte le format selon l'effectif et le temps que vous avez. Devis sur demande." },
          { question: "Et un EVG ou un EVJF ?", answer: "Très souvent demandé. La formule EVG / EVJF combine l'activité et un temps plus convivial sur place, avec une seule page dédiée dans le menu Formules." },
          { question: 'Comment je réserve ?', answer: "Au lancement, par la page contact ou par téléphone. La résa en ligne arrive prochainement, notre équipe travaille activement à sa mise en place." },
          { question: "Une session, ça dure combien ?", answer: "60 minutes pour une session classique. Les défis dans chaque salle font 3 minutes, donc on en enchaîne beaucoup pendant l'heure." },
          { question: "On joue à combien dans une équipe ?", answer: "De 2 à 5 idéalement. Au-delà, on monte plusieurs équipes en parallèle et on compare les scores à la fin." },
          { question: "À partir de quel âge ?", answer: "Dès 8 ans avec un adulte, dès 12 ans en autonomie. Pour les anniversaires d'enfants, précisez l'âge du groupe dans votre demande, le format sera adapté en conséquence." },
          { question: "Comment s'habiller ?", answer: "Tenue confortable, baskets. Certaines salles demandent de bouger, viser, se baisser. Les talons et la jupe trop courte, on évite pour être plus à l'aise." },
          { question: "Il faut être sportif ?", answer: "Pas du tout. La moitié des salles se gagnent par l'observation, la mémoire ou la logique. La personne qui pense être nulle au départ finit souvent par marquer un beau score quelque part." },
          { question: "Et pour un devis groupe ?", answer: "Pour team building, séminaire, privatisation, EVG, EVJF ou anniversaire : page contact avec l'effectif, la date souhaitée et le type d'événement. On revient avec une proposition." },
          { question: 'Vous prenez les Pass Culture ou ANCV ?', answer: "On accepte CB, espèces et Chèques-Vacances ANCV. Le Pass Culture, on étudie la possibilité d'intégration." },
          { question: "Le parking est facile ?", answer: "Oui, beaucoup de places de stationnement gratuites à proximité immédiate du complexe." },
          { question: "Si on est en retard ?", answer: "On essaie toujours de rattraper en glissant la session sur le créneau suivant si c'est possible. Mais si la partie ne peut pas se faire, le créneau n'est pas remboursable. On peut éventuellement le décaler ou émettre un avoir." },
          { question: "Comment on annule une résa ?", answer: "Toute réservation est définitive. On peut la décaler si vous nous prévenez au moins 48h à l'avance. Au-delà ça devient compliqué côté logistique." },
          { question: 'Pour une demande particulière ?', answer: "Appelez-nous ou écrivez via le formulaire. On lit tout, et un cas précis se gère mieux à l'oral." },
        ],
      },
    },
    actualites: {
      hero: {
        eyebrow: 'Actualités & événements',
        title: 'Actualités et événements Gameside Caen',
        subtitle: "Retrouvez toutes les actualités et événements à venir chez Gameside Caen : ouvertures, soirées privées, animations spéciales, offres de groupe et lancements des nouvelles activités à Ifs.",
        emptyTitle: 'Pas encore d\'actualités',
        emptyText: "On commencera à publier ici dès l'approche de l'ouverture. En attendant, le formulaire de contact reste le moyen le plus direct de nous joindre.",
        emptyCtaLabel: 'Nous contacter',
        emptyCtaHref: '/contact',
      },
    },
    contact: {
      intro: {
        eyebrow: 'GAMESIDE CAEN',
        title: 'CONTACT GAMESIDE CAEN',
        paragraphs: [
          "Une question, un projet d'événement, une demande de devis groupe ? On lit toutes les demandes : séminaire, team building, privatisation, anniversaire, EVG ou EVJF. On vous répond rapidement.",
          "Pour un cas précis ou un délai court, le téléphone reste le plus efficace. Sinon le formulaire fait très bien le boulot.",
        ],
        addressTitle: 'Adresse',
        addressNote: "(Adresse de l'établissement)",
        phoneTitle: 'Téléphone',
        contactTab: 'Contact',
        quoteTab: 'Demande de devis',
        submitLabel: 'ENVOYER',
        submittingLabel: 'ENVOI...',
        successMessage: 'Message envoyé',
        legalText: "En soumettant ce formulaire, j'accepte que les informations saisies soient utilisées dans le cadre de ma demande et de la relation qui peut en découler conformément à la Politique de confidentialité. Une vérification anti-spam technique peut être appliquée au moment de l'envoi.",
      },
    },
    legal: {
      mentions: {
        eyebrow: 'Informations légales',
        title: 'Mentions légales',
        intro: "Cette page présente les informations d’édition, de création, d’hébergement et d’utilisation du site Gameside Caen.",
        editorSectionTitle: 'Éditeur du site',
        editorNote: 'Les informations administratives de l’exploitant peuvent être complétées ou précisées sur demande du responsable du site.',
        creatorSectionTitle: 'Création et développement',
        hostingSectionTitle: 'Hébergement et infrastructure',
        hostingText: 'Cloudflare fournit l’infrastructure réseau, la protection et la diffusion du site. Certains services Cloudflare peuvent déposer des cookies strictement nécessaires à la sécurité et à la disponibilité du site.',
        usageSectionTitle: 'Conditions d’utilisation',
        usageParagraphs: [
          'Le site est accessible en continu, sauf interruption programmée ou non, nécessité de maintenance, cas de force majeure ou incident technique indépendant de la volonté de l’éditeur.',
          'Gameside Caen s’efforce de fournir des informations exactes et à jour. Les contenus, horaires, tarifs, visuels et disponibilités restent indicatifs et peuvent évoluer.',
        ],
        intellectualPropertySectionTitle: 'Propriété intellectuelle',
        intellectualPropertyText: 'Les textes, visuels, logos, mises en page, éléments graphiques et contenus du site sont protégés par le droit d’auteur et le droit des marques. Toute reproduction, représentation, adaptation ou diffusion non autorisée est interdite.',
        externalLinksSectionTitle: 'Liens externes',
        externalLinksText: 'Le site peut contenir des liens vers des services tiers, notamment Google Maps, YouTube, les réseaux sociaux, Cloudflare, le site de l’agence MAV ou le site de l’agence web Balrog. Gameside Caen ne contrôle pas ces sites tiers et ne peut être tenu responsable de leurs contenus ou de leurs pratiques.',
      },
      privacy: {
        eyebrow: 'Données personnelles',
        title: 'Politique de confidentialité',
        intro: 'Cette politique explique quelles données peuvent être traitées sur le site Gameside Caen, pour quelles finalités et avec quels droits pour les utilisateurs.',
        controllerSectionTitle: 'Responsable du traitement',
        dataSectionTitle: 'Données collectées',
        dataParagraphs: [
          'Lorsqu’un utilisateur contacte Gameside Caen, les informations transmises peuvent inclure le prénom, le nom, l’adresse e-mail, le téléphone, le message, la date souhaitée, le type d’événement et toute information utile à la demande.',
          'Des données techniques peuvent également être traitées pour assurer la sécurité, la disponibilité et le fonctionnement du site : adresse IP, navigateur, pages consultées, journaux serveur, choix de consentement cookies et données liées à l’administration du site.',
        ],
        purposesSectionTitle: 'Finalités et bases légales',
        purposesParagraphs: [
          'Les données sont utilisées pour répondre aux demandes de contact et de devis, préparer les réservations ou événements, assurer la sécurité du site, maintenir le service, respecter les obligations légales et améliorer la qualité des informations publiées.',
          'Selon le cas, les traitements reposent sur l’exécution de mesures précontractuelles, l’intérêt légitime de Gameside Caen, le consentement de l’utilisateur ou une obligation légale.',
        ],
        recipientsSectionTitle: 'Destinataires',
        recipientsText: 'Les données sont destinées à Gameside Caen. Elles peuvent être accessibles aux prestataires techniques nécessaires au fonctionnement du site, notamment MAV pour la création et la maintenance, Cloudflare pour l’infrastructure, et les services tiers affichés uniquement après consentement lorsqu’ils sont optionnels.',
        retentionSectionTitle: 'Durées de conservation',
        retentionText: 'Les demandes de contact et de devis sont conservées pendant la durée nécessaire au traitement de la demande, puis jusqu’à 3 ans après le dernier échange commercial. Le cookie de consentement est conservé {{COOKIE_DAYS}} jours. La session d’administration est limitée à 8 heures. Les journaux techniques sont conservés pour une durée proportionnée aux besoins de sécurité et de maintenance.',
        cookiesSectionTitle: 'Cookies et contenus tiers',
        cookiesParagraphs: [
          'Le site utilise Google Analytics 4 pour mesurer son audience et Google Ads pour suivre les conversions, uniquement après acceptation des cookies optionnels. Google Maps et YouTube sont également bloqués tant que ces cookies ne sont pas acceptés.',
          'Le détail des cookies et les boutons de gestion sont disponibles dans la politique de cookies.',
        ],
        rightsSectionTitle: 'Vos droits',
        rightsParagraphs: [
          'Vous pouvez demander l’accès, la rectification, l’effacement, l’opposition, la limitation ou la portabilité de vos données lorsque ces droits s’appliquent. Vous pouvez également retirer votre consentement aux cookies optionnels à tout moment.',
          'En cas de difficulté, vous pouvez saisir la CNIL depuis cnil.fr.',
        ],
      },
      cookies: {
        eyebrow: 'Cookies et traceurs',
        title: 'Politique de cookies (UE)',
        intro: 'Cette page décrit les cookies utilisés sur le site Gameside Caen et permet de modifier votre consentement.',
        generalSectionTitle: 'Principe général',
        generalText: 'Un cookie est un petit fichier enregistré sur votre navigateur. Certains cookies sont nécessaires au fonctionnement du site. D’autres, liés à la mesure d’audience Google Analytics 4, au suivi des conversions Google Ads ou à des contenus tiers comme Google Maps et YouTube, ne sont chargés qu’après votre accord.',
        usedSectionTitle: 'Cookies utilisés',
        refusedSectionTitle: 'Cookies refusés par défaut',
        refusedText: 'Tant que vous n’avez pas accepté les cookies optionnels, le site ne charge ni Google Analytics 4, ni le suivi des conversions Google Ads, ni les iframes Google Maps et YouTube. À la place, une zone d’attente vous permet d’accepter les cookies si vous souhaitez activer ces services.',
        browserSectionTitle: 'Gestion dans le navigateur',
        browserText: 'Vous pouvez supprimer les cookies depuis les réglages de votre navigateur. Si vous supprimez le cookie de consentement, le bandeau s’affichera à nouveau lors d’une prochaine visite.',
      },
    },
  };
}

function createBusinessPageDefaults(config: {
  images: { hero: string; intro: string; gallery: string[] };
  eyebrow: string;
  heroTitle: string;
  heroTitleAccent: string;
  heroSubtitle: string;
  watermarkText: string;
  primaryCtaLabel?: string;
  primaryCtaHref?: string;
  secondaryCtaLabel?: string;
  introEyebrow: string;
  introTitle: string;
  paragraphs: string[];
  questionTitle: string;
  questionText: string;
  formatsEyebrow: string;
  formatsTitle: string;
  formatsText: string;
  formats: EditableItem[];
  galleryEyebrow?: string;
  galleryTitle: string;
  galleryText: string;
  galleryCtaLabel?: string;
  galleryCtaHref?: string;
  gallerySecondaryLabel?: string;
  faqTitle?: string;
  faqItems?: EditableItem[];
  timelineItems?: EditableItem[];
}): EditablePageContent {
  return {
    hero: {
      image: config.images.hero,
      eyebrow: config.eyebrow,
      title: config.heroTitle,
      titleAccent: config.heroTitleAccent,
      subtitle: config.heroSubtitle,
      watermark: config.watermarkText,
      primaryCtaLabel: config.primaryCtaLabel || 'Demander un devis',
      primaryCtaHref: config.primaryCtaHref || '/contact',
      secondaryCtaLabel: config.secondaryCtaLabel || 'Appeler',
    },
    intro: {
      image: config.images.intro,
      eyebrow: config.introEyebrow,
      title: config.introTitle,
      paragraphs: config.paragraphs,
      questionTitle: config.questionTitle,
      questionText: config.questionText,
    },
    formats: {
      eyebrow: config.formatsEyebrow,
      title: config.formatsTitle,
      text: config.formatsText,
      ambientImage: config.images.gallery[0] || config.images.intro,
      items: config.formats,
    },
    gallery: {
      eyebrow: config.galleryEyebrow || 'Galerie entreprises',
      title: config.galleryTitle,
      text: config.galleryText,
      images: config.images.gallery,
      ctaLabel: config.galleryCtaLabel || 'Demander un devis',
      ctaHref: config.galleryCtaHref || '/contact',
      secondaryCtaLabel: config.gallerySecondaryLabel || 'Appeler',
    },
    faq: {
      title: config.faqTitle || 'Questions fréquentes',
      items: config.faqItems || [],
    },
    timeline: {
      items: config.timelineItems || [],
    },
    selection: {
      title: 'Ce que la formule inclut',
      items: [
        { title: 'Activités uniques', text: "Une sélection d'activités uniques en France et adaptées à tous", image: '/brand/business/extraordinary.png' },
        { title: 'Restauration', text: 'De nombreuses possibilités de restauration pour votre événement.', image: '/brand/business/food.png' },
        { title: 'Prestations', text: 'Des prestations pour tous les goûts : Concert, Animation, Magicien…', image: '/brand/business/live.png' },
      ],
    },
  };
}

function createFormulaPageDefaults(config: {
  images: { hero: string; feature: string; planningImages?: string[] };
  heroTitle: string;
  subtitle: string;
  watermark: string;
  introTitle: string;
  introBadge: string;
  introLead: string;
  introParagraphs: string[];
  planningEyebrow: string;
  planningTitle: string;
  planningText: string;
  planningItems: EditableItem[];
  timelineItems?: EditableItem[];
  selectionTitle: string;
  faqTitle?: string;
  faqItems?: EditableItem[];
}): EditablePageContent {
  return {
    hero: {
      image: config.images.hero,
      eyebrow: 'EAT DRINK HAVE FUN !',
      title: config.heroTitle,
      subtitle: config.subtitle,
      watermark: config.watermark,
    },
    intro: {
      image: config.images.feature,
      title: config.introTitle,
      badge: config.introBadge,
      lead: config.introLead,
      paragraphs: config.introParagraphs,
      ctaLabel: 'DEMANDER UN DEVIS',
      ctaHref: '/contact',
    },
    planning: {
      eyebrow: config.planningEyebrow,
      title: config.planningTitle,
      text: config.planningText,
      watermark: config.watermark,
      images: config.images.planningImages || [],
      items: config.planningItems,
    },
    selection: {
      title: config.selectionTitle,
      items: [
        { title: 'Action Game', subtitle: 'ACTION GAME', text: 'Des salles immersives, des défis courts et un score à aller chercher.' },
        { title: 'Team', subtitle: 'GROUPE', text: 'Un format qui mélange les profils et garde tout le monde ensemble.' },
        { title: 'Challenge', subtitle: 'SCORES', text: 'Une compétition bon enfant pour comparer les équipes jusqu’au bout.' },
        { title: 'Souvenirs', subtitle: 'MOMENT FORT', text: 'Des réussites, des ratés et de quoi refaire le match après la partie.' },
      ],
    },
    faq: {
      title: config.faqTitle || 'Questions fréquentes',
      items: config.faqItems || [],
    },
    timeline: {
      items: config.timelineItems || [],
    },
  };
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function mergeEditableValue(base: EditableSectionValue, source: unknown): EditableSectionValue {
  if (Array.isArray(base)) {
    if (!Array.isArray(source)) {
      return base;
    }

    if (source.length === 0) {
      return [];
    }

    if (typeof base[0] === 'string' || (base.length === 0 && typeof source[0] === 'string')) {
      return source.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
    }

    return source
      .filter(isPlainObject)
      .map((item, index) => ({
        ...((base[index] as EditableItem | undefined) || {}),
        ...(item as EditableItem),
      }));
  }

  if (typeof source === 'string') {
    return source;
  }

  return base;
}

export function mergePageContent(defaultPages: SitePagesContent, source?: Partial<SitePagesContent> | null): SitePagesContent {
  const merged = JSON.parse(JSON.stringify(defaultPages)) as SitePagesContent;

  if (!source || !isPlainObject(source)) {
    return merged;
  }

  for (const [pageKey, pageContent] of Object.entries(source)) {
    if (!isPlainObject(pageContent)) {
      continue;
    }

    if (!merged[pageKey]) {
      merged[pageKey] = {};
    }

    for (const [sectionKey, sectionContent] of Object.entries(pageContent)) {
      if (!isPlainObject(sectionContent)) {
        continue;
      }

      const section = merged[pageKey][sectionKey] || {};
      merged[pageKey][sectionKey] = section;

      for (const [fieldKey, value] of Object.entries(sectionContent)) {
        if (fieldKey in section) {
          section[fieldKey] = mergeEditableValue(section[fieldKey], value);
        }
      }
    }
  }

  return merged;
}

const heroFields: AdminPageField[] = [
  image('image', 'Photo principale', 'Grande image affichée en haut de la page'),
  text('eyebrow', "Texte d'accroche", 'Petit texte affiché au-dessus du titre'),
  textarea('title', 'Titre'),
  text('titleLine1', 'Titre — ligne 1'),
  text('titleLine2', 'Titre — ligne 2'),
  text('titleAccent', 'Mot mis en valeur', "Ce mot du titre s'affiche dans une couleur différente"),
  textarea('subtitle', 'Sous-titre'),
  text('watermark', 'Mot décoratif', 'Grand mot semi-transparent en arrière-plan (effet graphique uniquement)'),
  text('primaryCtaLabel', 'Texte du bouton principal'),
  text('primaryCtaHref', "URL du bouton principal", 'Ex : /contact ou /nos-activites/action-game'),
  text('secondaryCtaLabel', 'Texte du bouton secondaire'),
  text('secondaryCtaHref', 'URL du bouton secondaire'),
];

const businessSections: AdminPageSection[] = [
  {
    key: 'hero',
    title: 'Hero',
    fields: [image('image', 'Image hero'), text('eyebrow', "Texte d'accroche"), text('title', 'Titre'), text('titleAccent', 'Titre accent'), textarea('subtitle', 'Sous-titre'), text('watermark', "Mot décoratif"), text('primaryCtaLabel', 'Bouton principal'), text('primaryCtaHref', 'Lien bouton principal'), text('secondaryCtaLabel', 'Bouton téléphone')],
  },
  {
    key: 'intro',
    title: 'Introduction',
    fields: [image('image', 'Image intro'), text('eyebrow', "Texte d'accroche"), text('title', 'Titre'), textList('paragraphs', 'Paragraphes'), text('questionTitle', 'Titre encadré'), textarea('questionText', 'Texte encadré')],
  },
  {
    key: 'formats',
    title: 'Formats',
    fields: [image('ambientImage', 'Image d’ambiance'), text('eyebrow', "Texte d'accroche"), textarea('title', 'Titre'), textarea('text', 'Texte'), items('items', 'Cartes formats', titleTextImageFields)],
  },
  {
    key: 'gallery',
    title: 'Galerie',
    fields: [text('eyebrow', "Texte d'accroche"), text('title', 'Titre'), textarea('text', 'Texte'), imageList('images', 'Images galerie'), text('ctaLabel', 'Bouton principal'), text('ctaHref', 'Lien bouton principal'), text('secondaryCtaLabel', 'Bouton téléphone')],
  },
  {
    key: 'timeline',
    title: 'Exemple de journée',
    fields: [items('items', 'Étapes de la journée', timelineStepFields, 'Laissez vide pour utiliser le programme par défaut.')],
  },
  {
    key: 'selection',
    title: 'Ce que la formule inclut',
    fields: [
      text('title', 'Titre'),
      items('items', 'Points forts (3 cartes)', [
        { key: 'title', label: 'Titre interne (non affiché)' },
        { key: 'text', label: 'Texte affiché', type: 'textarea' },
        { key: 'image', label: 'Image / pictogramme', type: 'image' },
      ]),
    ],
  },
  {
    key: 'faq',
    title: 'FAQ',
    fields: [text('title', 'Titre'), items('items', 'Questions', faqFields)],
  },
];

const formulaSections: AdminPageSection[] = [
  {
    key: 'hero',
    title: 'Hero',
    fields: [image('image', 'Image hero'), text('eyebrow', "Texte d'accroche"), textarea('title', 'Titre'), textarea('subtitle', 'Sous-titre'), text('watermark', "Mot décoratif")],
  },
  {
    key: 'intro',
    title: 'Introduction',
    fields: [image('image', 'Image intro'), textarea('title', 'Titre'), text('badge', 'Badge'), text('lead', 'Phrase d’accroche'), textList('paragraphs', 'Paragraphes'), text('ctaLabel', 'Bouton'), text('ctaHref', 'Lien bouton')],
  },
  {
    key: 'planning',
    title: 'Organisation',
    fields: [text('eyebrow', "Texte d'accroche"), text('title', 'Titre'), textarea('text', 'Texte'), text('watermark', "Mot décoratif"), imageList('images', 'Photos (4 images)'), items('items', 'Cartes organisation', titleTextFields)],
  },
  {
    key: 'timeline',
    title: 'Déroulé de session',
    fields: [items('items', 'Étapes de la session', timelineStepFields, 'Chaque étape peut utiliser une image pictogramme. L’icône Lucide sert seulement de secours si aucune image n’est définie.')],
  },
  {
    key: 'selection',
    title: 'Sélection',
    fields: [text('title', 'Titre'), items('items', 'Temps forts', selectionFields)],
  },
  {
    key: 'faq',
    title: 'FAQ',
    fields: [text('title', 'Titre'), items('items', 'Questions', faqFields)],
  },
];

export const adminPageGroups: AdminPageGroup[] = [
  {
    key: 'home',
    title: 'Accueil',
    path: '/',
    sections: [
      { key: 'hero', title: 'Hero', fields: [image('image', 'Image hero'), text('titleLine1', 'Titre ligne 1'), text('titleLine2', 'Titre ligne 2'), textarea('subtitle', 'Sous-titre'), text('primaryCtaLabel', 'Bouton principal'), text('primaryCtaHref', 'Lien principal'), text('secondaryCtaLabel', 'Bouton secondaire'), text('secondaryCtaHref', 'Lien secondaire')] },
      { key: 'activities', title: 'Activités', fields: [textarea('title', 'Titre'), textarea('text', 'Texte'), image('actionImage', 'Image Action Game'), image('actionLogo', 'Logo Action Game'), text('actionTitle', 'Titre carte Action Game'), textarea('actionText', 'Texte carte Action Game'), items('soonItems', 'Cartes bientôt disponibles', titleTextFields)] },
      { key: 'stats', title: 'Statistiques', fields: [image('image', 'Image bannière'), items('items', 'Statistiques', statFields)] },
      { key: 'formulas', title: 'Formules', fields: [textarea('title', 'Titre'), textarea('text', 'Texte'), items('cards', 'Cartes formules', [...titleTextImageFields, { key: 'link', label: 'Lien' }, { key: 'cta', label: 'Bouton' }, { key: 'bannerColor', label: 'Couleur CSS' }])] },
      { key: 'local', title: 'Bloc Gameside à Caen', fields: [image('poster', 'Image / poster vidéo'), image('bgImage', "Image de fond (parallax)"), text('videoUrl', 'URL vidéo (YouTube embed)'), text('scrollHint', 'Indication de scroll'), text('title', 'Titre'), textList('paragraphs', 'Paragraphes'), text('ctaLabel', 'Bouton'), text('ctaHref', 'Lien bouton')] },
      { key: 'social', title: 'Réseaux sociaux', fields: [text('title', 'Titre'), text('instagramLabel', 'Libellé Instagram'), text('instagramHref', 'Lien Instagram'), text('facebookLabel', 'Libellé Facebook'), text('facebookHref', 'Lien Facebook')] },
    ],
  },
  {
    key: 'actionGame',
    title: 'Action Game',
    path: '/nos-activites/action-game',
    sections: [
      { key: 'hero', title: 'Hero', fields: heroFields },
      { key: 'intro', title: 'Introduction', fields: [image('image', 'Image intro'), text('eyebrow', "Texte d'accroche"), text('title', 'Titre'), text('titleAccent', 'Titre accent'), textList('paragraphs', 'Paragraphes')] },
      { key: 'stats', title: 'Statistiques', fields: [image('image', 'Image bannière'), items('items', 'Statistiques', statFields)] },
      { key: 'reservation', title: 'Réservation', fields: [text('eyebrow', "Texte d'accroche"), text('title', 'Titre')] },
      { key: 'howItWorks', title: 'Comment ça marche', fields: [text('eyebrow', "Texte d'accroche"), textarea('title', 'Titre'), items('items', 'Étapes', [{ key: 'step', label: 'Numéro' }, ...titleTextFields, { key: 'image', label: 'Image de fond', type: 'image' }])] },
      { key: 'pricing', title: 'Tarifs', fields: [text('title', 'Titre'), text('titleAccent', 'Titre accent'), textarea('text', 'Texte'), image('adultImage', 'Image adultes'), text('adultTitle', 'Titre adultes'), items('adultRows', 'Tarifs adultes', priceRowFields), image('juniorImage', 'Image juniors'), text('juniorTitle', 'Titre juniors'), items('juniorRows', 'Tarifs juniors', priceRowFields), text('juniorNote', 'Note junior')] },
      { key: 'birthdayCta', title: 'CTA anniversaire', fields: [text('title', 'Titre'), text('titleAccent', 'Titre accent'), textarea('text', 'Texte'), text('ctaLabel', 'Bouton'), text('ctaHref', 'Lien bouton')] },
      { key: 'universes', title: 'Univers', fields: [text('eyebrow', "Texte d'accroche"), text('title', 'Titre'), textarea('text', 'Texte'), items('items', 'Univers mis en avant', [{ key: 'title', label: 'Titre' }, { key: 'badge', label: 'Badge' }, { key: 'image', label: 'Image', type: 'image' }]), image('moreImage', 'Image autres salles'), text('moreEyebrow', "Accroche autres salles"), text('moreTitle', 'Titre autres salles'), textarea('moreText', 'Texte autres salles')] },
      { key: 'gallery', title: 'Galerie', fields: [text('title', 'Titre'), textarea('text', 'Texte'), imageList('images', 'Images')] },
      { key: 'eventCta', title: 'Événements groupe', fields: [text('title', 'Titre'), textarea('text', 'Texte'), text('ctaLabel', 'Bouton'), text('ctaHref', 'Lien bouton')] },
      { key: 'faq', title: 'FAQ', fields: [text('title', 'Titre'), items('items', 'Questions', faqFields)] },
    ],
  },
  {
    key: 'karaoke',
    title: 'Karaoké',
    path: '/nos-activites/karaoke',
    sections: [
      { key: 'card', title: 'Carte accueil & menus', fields: [image('image', 'Photo de la carte'), text('title', 'Titre de la carte'), textarea('text', 'Texte de la carte')] },
      { key: 'hero', title: 'Haut de page', fields: [image('image', 'Photo principale'), text('imageAlt', 'Description de la photo'), textarea('title', 'Titre'), textarea('subtitle', 'Sous-titre'), textarea('description', 'Description'), text('primaryCtaLabel', 'Bouton principal'), text('primaryCtaHref', 'Lien principal'), text('secondaryCtaLabel', 'Bouton secondaire'), text('secondaryCtaHref', 'Lien secondaire')] },
      { key: 'stats', title: 'Points clés', fields: [items('items', 'Points clés', [{ key: 'value', label: 'Valeur' }, { key: 'label', label: 'Libellé' }])] },
      { key: 'intro', title: 'Présentation de la salle', fields: [image('image', 'Photo de la salle', 'Ajoutez la photo de votre salle pour remplacer le visuel « Image bientôt disponible ».'), text('imageAlt', 'Description de la photo'), text('eyebrow', "Texte d'accroche"), text('title', 'Titre'), text('titleAccent', 'Titre accent'), textList('paragraphs', 'Paragraphes')] },
      { key: 'reservation', title: 'Réservation', fields: [text('eyebrow', "Texte d'accroche"), text('title', 'Titre')] },
      { key: 'howItWorks', title: 'Comment ça marche', fields: [text('eyebrow', "Texte d'accroche"), text('title', 'Titre'), items('items', 'Étapes', [{ key: 'step', label: 'Numéro' }, ...titleTextFields])] },
      { key: 'occasions', title: 'Soirées & événements', fields: [text('title', 'Titre'), text('titleAccent', 'Titre accent'), textarea('text', 'Texte'), items('items', 'Occasions', titleTextImageFields)] },
      { key: 'gallery', title: 'Galerie photos', fields: [text('eyebrow', "Texte d'accroche"), text('title', 'Titre'), textarea('text', 'Texte'), imageList('images', 'Photos', 'Ajoutez vos photos pour remplacer les visuels en attente. Une URL par ligne, 12 photos affichées au maximum.'), text('ctaLabel', 'Bouton'), text('ctaHref', 'Lien bouton')] },
      { key: 'birthdayCta', title: 'Anniversaires & groupes', fields: [image('image', 'Photo'), text('imageAlt', 'Description de la photo'), text('eyebrow', "Texte d'accroche"), text('title', 'Titre'), text('titleAccent', 'Titre accent'), textList('paragraphs', 'Paragraphes'), text('ctaLabel', 'Bouton'), text('ctaHref', 'Lien bouton')] },
      { key: 'features', title: 'Équipements & expérience', fields: [text('title', 'Titre'), items('items', 'Points forts', titleTextFields)] },
      { key: 'faq', title: 'FAQ', fields: [text('title', 'Titre'), items('items', 'Questions', faqFields)] },
    ],
  },
  { key: 'teamBuilding', title: 'Team building', path: '/entreprises/team-building', sections: businessSections },
  { key: 'privatisation', title: 'Privatisation', path: '/entreprises/privatisation', sections: businessSections },
  { key: 'seminaire', title: 'Séminaire', path: '/entreprises/seminaire', sections: businessSections },
  { key: 'evgEvjf', title: 'EVG / EVJF', path: '/formules/evg-evjf', sections: formulaSections },
  { key: 'anniversaireEnfant', title: 'Anniversaire enfant', path: '/formules/anniversaire-enfant', sections: formulaSections },
  {
    key: 'infosPratiques',
    title: 'Infos pratiques',
    path: '/infos-pratiques',
    sections: [
      { key: 'hero', title: 'Hero', fields: [image('image', 'Image hero'), textarea('title', 'Titre'), textarea('subtitle', 'Sous-titre')] },
      { key: 'faq', title: 'FAQ', fields: [text('title', 'Titre'), textarea('subtitle', 'Sous-titre'), text('ctaLabel', 'Bouton'), text('ctaHref', 'Lien bouton'), items('items', 'Questions', faqFields)] },
    ],
  },
  {
    key: 'actualites',
    title: 'Actualités',
    path: '/actualites',
    sections: [
      { key: 'hero', title: 'Hero', fields: [text('eyebrow', "Texte d'accroche"), textarea('title', 'Titre'), textarea('subtitle', 'Sous-titre'), text('emptyTitle', 'Titre sans actualité'), textarea('emptyText', 'Texte sans actualité'), text('emptyCtaLabel', 'Bouton sans actualité'), text('emptyCtaHref', 'Lien bouton')] },
    ],
  },
  {
    key: 'contact',
    title: 'Contact',
    path: '/contact',
    sections: [
      { key: 'intro', title: 'Contenu', fields: [text('eyebrow', "Texte d'accroche"), text('title', 'Titre'), textList('paragraphs', 'Paragraphes'), text('addressTitle', 'Titre adresse'), text('addressNote', 'Note adresse'), text('phoneTitle', 'Titre téléphone'), text('contactTab', 'Onglet contact'), text('quoteTab', 'Onglet devis'), text('submitLabel', 'Bouton envoi'), text('submittingLabel', 'Bouton pendant envoi'), textarea('successMessage', 'Message succès'), textarea('legalText', 'Texte RGPD')] },
    ],
  },
  {
    key: 'legal',
    title: 'Pages légales',
    path: '/mentions-legales',
    sections: [
      {
        key: 'mentions',
        title: 'Mentions légales',
        fields: [
          text('eyebrow', "Texte d'accroche"),
          text('title', 'Titre'),
          textarea('intro', 'Intro'),
          text('editorSectionTitle', 'Titre éditeur'),
          textarea('editorNote', 'Note éditeur'),
          text('creatorSectionTitle', 'Titre création'),
          text('hostingSectionTitle', 'Titre hébergement'),
          textarea('hostingText', 'Texte hébergement'),
          text('usageSectionTitle', 'Titre conditions'),
          textList('usageParagraphs', 'Paragraphes conditions'),
          text('intellectualPropertySectionTitle', 'Titre propriété intellectuelle'),
          textarea('intellectualPropertyText', 'Texte propriété intellectuelle'),
          text('externalLinksSectionTitle', 'Titre liens externes'),
          textarea('externalLinksText', 'Texte liens externes'),
        ],
      },
      {
        key: 'privacy',
        title: 'Confidentialité',
        fields: [
          text('eyebrow', "Texte d'accroche"),
          text('title', 'Titre'),
          textarea('intro', 'Intro'),
          text('controllerSectionTitle', 'Titre responsable'),
          text('dataSectionTitle', 'Titre données collectées'),
          textList('dataParagraphs', 'Paragraphes données collectées'),
          text('purposesSectionTitle', 'Titre finalités'),
          textList('purposesParagraphs', 'Paragraphes finalités'),
          text('recipientsSectionTitle', 'Titre destinataires'),
          textarea('recipientsText', 'Texte destinataires'),
          text('retentionSectionTitle', 'Titre conservation'),
          textarea('retentionText', 'Texte conservation'),
          text('cookiesSectionTitle', 'Titre cookies'),
          textList('cookiesParagraphs', 'Paragraphes cookies'),
          text('rightsSectionTitle', 'Titre droits'),
          textList('rightsParagraphs', 'Paragraphes droits'),
        ],
      },
      {
        key: 'cookies',
        title: 'Cookies',
        fields: [
          text('eyebrow', "Texte d'accroche"),
          text('title', 'Titre'),
          textarea('intro', 'Intro'),
          text('generalSectionTitle', 'Titre principe'),
          textarea('generalText', 'Texte principe'),
          text('usedSectionTitle', 'Titre cookies utilisés'),
          text('refusedSectionTitle', 'Titre refus'),
          textarea('refusedText', 'Texte refus'),
          text('browserSectionTitle', 'Titre navigateur'),
          textarea('browserText', 'Texte navigateur'),
        ],
      },
    ],
  },
];
